<template>
  <div>
    <b-card no-body>
      <div class="mx-2 mt-2">
        <!-- Table Top -->
        <ResourceListHeader
          :resource="RESOURCES.BUSINESS"
          :search-query="filters.search"
          @search="updateSearchQuery"
          @add="openModal"
          @export="exportData"
          :is-exporting="isExporting"
          :watch-link="businessVideoLink"
          :hideWatch="false"
        />
      </div>

      <!-- Loading Overlay for Actions -->
      <div v-if="isActionLoading" class="action-loading-overlay">
        <div class="loading-content">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <p class="loading-text mt-2">Loading business details...</p>
        </div>
      </div>

      <!-- Table with Pagination -->
      <b-table
        ref="businessTable"
        :items="businesses"
        :fields="tableColumns"
        responsive
        show-empty
        striped
        :busy="isLoading"
      >
        <!-- Loading slot with shimmer effect -->
        <template #table-busy class="p-0">
          <TableSkeleton :columns="tableColumns" :rows="5" />
        </template>

        <!-- Column: Serial Number -->
        <template #cell(serial)="data">
          {{ data.item.serial }}
        </template>

        <!-- Column: ID -->
        <template #cell(id)="data"> {{ data.item.id }} </template>

        <!-- Column: Business Name -->
        <template #cell(businessName)="data">
          {{ data.item.businessName || 'N/A' }}
        </template>

        <!-- Column: NTN Number -->
        <template #cell(ntnNumber)="data">
          {{ data.item.ntnNumber || 'N/A' }}
        </template>

        <!-- Column: STRN Number -->
        <template #cell(strn)="data">
          {{ data.item.strn || 'N/A' }}
        </template>

        <!-- Column: Phone -->
        <template #cell(phone)="data">
          {{ data.item.phone || 'N/A' }}
        </template>

        <!-- Column: Email -->
        <template #cell(email)="data">
          {{ data.item.email || 'N/A' }}
        </template>

        <!-- Column: Province -->
        <template #cell(province)="data">
          {{ data.item.province || 'N/A' }}
        </template>

        <!-- Column: Run Test Case -->
        <template #cell(runTest)="data">
          <b-button
            size="sm"
            variant="outline-primary"
            @click="runTestCase(data.item)"
            title="Run test case for this business"
            class="run-test-btn"
          >
            <feather-icon icon="PlayIcon" size="14" class="mr-1" />
            Test
          </b-button>
        </template>

        <!-- Column: Custom Test -->
        <template #cell(customTest)="data">
          <b-button
            size="sm"
            variant="outline-info"
            @click="runCustomTestCase(data.item)"
            title="Run custom test case"
            class="custom-test-btn"
          >
            <feather-icon icon="SettingsIcon" size="14" class="mr-1" />
            Custom
          </b-button>
        </template>

        <!-- Column: Update Password -->
        <template #cell(updatePassword)="data">
          <b-button
            type="button"
            size="sm"
            variant="outline-primary"
            @click.stop="openUpdatePasswordModal(data.item)"
            title="Update Password"
            class="update-password-btn"
          >
            <feather-icon icon="KeyIcon" size="14" />
            <span>Update</span>
          </b-button>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <action-cell
            :resource="RESOURCES.BUSINESS"
            :selected-resource-name="data.item.businessName"
            has-view-action
            :has-edit-action="false"
            :has-delete-action="false"
            @viewAction="viewBusiness(data.item)"
          />
        </template>
      </b-table>

      <!-- Pagination -->
      <div class="mx-2 mb-2 d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <label class="mb-0 mr-1">Show</label>
          <b-form-select
            v-model="filters.perPage"
            :options="pageOptions"
            size="sm"
            class="w-auto pagination-select"
            @change="handlePerPageChange"
          />
          <label class="mb-0 ml-1">entries</label>
        </div>
        <div class="pagination-info text-muted">
          {{ paginationInfo }}
        </div>
        <b-pagination
          v-model="filters.currentPage"
          :total-rows="pagination.totalRows"
          :per-page="filters.perPage"
          class="mb-0 pagination-modern"
          @change="handlePageChange"
        >
          <template #prev-text>
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </template>
          <template #next-text>
            <feather-icon icon="ChevronRightIcon" size="18" />
          </template>
        </b-pagination>
      </div>
    </b-card>

    <business-modal
      :is-editing="isEditing"
      :view-detail="viewDetail"
      :business="business"
      :business-i-d="businessID"
      :user-i-d="userID"
      @saved="handleBusinessSaved"
      @close="handleBusinessModalClose"
      ref="businessModal"
    />

    <!-- Test Results Modal -->
    <b-modal
      v-model="showTestResultsModal"
      title="Test Case Results"
      size="lg"
      :hide-footer="true"
      class="test-results-modal"
      scrollable
    >
      <div v-if="testResults" class="test-results-enhanced">
        <!-- Enhanced Summary Header -->
        <div class="results-summary-enhanced">
          <div class="summary-header-enhanced" :class="getSummaryClass()">
            <div class="summary-icon-container">
              <feather-icon :icon="getSummaryIcon()" size="28" class="summary-icon" />
            </div>
            <div class="summary-content">
              <h4 class="summary-title">{{ getSummaryTitle() }}</h4>
              <p class="summary-message">{{ testResults.message }}</p>
            </div>
          </div>
        </div>

        <!-- Test Execution Report - Enhanced -->
        <div class="test-execution-report">
          <div class="report-header">
            <div class="report-title-section">
              <div class="report-icon">
                <feather-icon icon="FileTextIcon" size="24" />
              </div>
              <div class="report-content">
                <h5 class="report-title">Test Execution Report</h5>
                <p class="report-subtitle">Detailed analysis of test case validation results</p>
              </div>
            </div>
            <div class="test-stats-grid">
              <div class="stat-card total">
                <div class="stat-icon">
                  <feather-icon icon="CheckSquareIcon" size="20" />
                </div>
                <div class="stat-content">
                  <span class="stat-number">{{ testResults.data.validationResults.length }}</span>
                  <span class="stat-label">Total Tests</span>
                </div>
              </div>
              <div class="stat-card failed">
                <div class="stat-icon">
                  <feather-icon icon="XCircleIcon" size="20" />
                </div>
                <div class="stat-content">
                  <span class="stat-number">{{
                    testResults.data.validationResults.filter(
                      (t) => t.status === 'error' || t.status === 'Invalid'
                    ).length
                  }}</span>
                  <span class="stat-label">Failed</span>
                </div>
              </div>
              <div class="stat-card passed">
                <div class="stat-icon">
                  <feather-icon icon="CheckCircleIcon" size="20" />
                </div>
                <div class="stat-content">
                  <span class="stat-number">{{
                    testResults.data.validationResults.filter((t) => t.status === 'success').length
                  }}</span>
                  <span class="stat-label">Passed</span>
                </div>
              </div>
            </div>
          </div>

          <div class="execution-summary-enhanced">
            <div class="summary-card-enhanced">
              <div class="summary-header-enhanced">
                <div class="summary-icon">
                  <feather-icon icon="BarChart3Icon" size="20" />
                </div>
                <h6 class="summary-title">Execution Summary</h6>
              </div>
              <div class="summary-details-grid">
                <div class="summary-detail-item">
                  <div class="detail-icon">
                    <feather-icon icon="LayersIcon" size="16" />
                  </div>
                  <div class="detail-content">
                    <span class="detail-label">Test Suite</span>
                    <span class="detail-value">FBR Integration Validation</span>
                  </div>
                </div>
                <div class="summary-detail-item">
                  <div class="detail-icon">
                    <feather-icon icon="ClockIcon" size="16" />
                  </div>
                  <div class="detail-content">
                    <span class="detail-label">Execution Time</span>
                    <span class="detail-value">{{ formatServerTime(testResults.serverTime) }}</span>
                  </div>
                </div>
                <div class="summary-detail-item">
                  <div class="detail-icon">
                    <feather-icon icon="TrendingUpIcon" size="16" />
                  </div>
                  <div class="detail-content">
                    <span class="detail-label">Success Rate</span>
                    <span class="detail-value success-rate">
                      {{
                        Math.round(
                          (testResults.data.validationResults.filter((t) => t.status === 'success')
                            .length /
                            testResults.data.validationResults.length) *
                            100
                        )
                      }}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="individual-test-results">
            <div class="results-header">
              <div class="results-title-section">
                <div class="results-icon">
                  <feather-icon icon="ListIcon" size="20" />
                </div>
                <div class="results-content">
                  <h6 class="results-title">Individual Test Results</h6>
                  <p class="results-subtitle">
                    {{ testResults.data.validationResults.length }} test cases analyzed
                  </p>
                </div>
              </div>
            </div>

            <div class="test-cases-grid">
              <div
                v-for="testCase in testResults.data.validationResults"
                :key="testCase.testCaseId"
                class="test-case-card"
                :class="getTestCaseClass(testCase.status)"
              >
                <div class="test-case-header">
                  <div class="test-case-number">
                    <span class="number-badge">{{ testCase.testCaseId }}</span>
                  </div>
                  <div class="test-case-status">
                    <div class="status-badge" :class="getStatusClass(testCase.status)">
                      <feather-icon
                        :icon="getStatusIcon(testCase.status)"
                        size="16"
                        class="status-icon"
                      />
                      <span class="status-text">{{ getStatusText(testCase.status) }}</span>
                    </div>
                  </div>
                </div>
                <div class="test-case-body">
                  <h6 class="test-case-title">Test Case {{ testCase.testCaseId }}</h6>
                  <p class="test-case-message">{{ testCase.message }}</p>
                  <div class="test-case-meta">
                    <span class="test-timestamp">
                      <feather-icon icon="ClockIcon" size="14" class="meta-icon" />
                      {{ formatTimestamp(testCase) }}
                    </span>
                  </div>
                </div>

                <div v-if="testCase.fbrResponse" class="test-case-details">
                  <div class="details-header">
                    <feather-icon icon="CodeIcon" size="16" class="details-icon" />
                    <span class="details-title">FBR Response Details</span>
                  </div>
                  <div class="details-content">
                    <!-- Invoice Information for Success Cases -->
                    <div v-if="testCase.fbrResponse.invoiceNumber" class="invoice-info mb-3">
                      <h6 class="invoice-title">Invoice Generated Successfully</h6>
                      <div class="invoice-details">
                        <div class="detail-row">
                          <span class="detail-label">Invoice Number:</span>
                          <span class="detail-value">{{ testCase.fbrResponse.invoiceNumber }}</span>
                        </div>
                        <div class="detail-row">
                          <span class="detail-label">Date:</span>
                          <span class="detail-value">{{ testCase.fbrResponse.dated }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Validation Response Details -->
                    <div
                      v-if="testCase.fbrResponse.validationResponse"
                      class="validation-details mb-3"
                    >
                      <h6 class="validation-title">Validation Response</h6>
                      <div class="validation-info">
                        <div class="detail-row">
                          <span class="detail-label">Status Code:</span>
                          <span class="detail-value">{{
                            testCase.fbrResponse.validationResponse.statusCode
                          }}</span>
                        </div>
                        <div class="detail-row">
                          <span class="detail-label">Status:</span>
                          <span
                            class="detail-value"
                            :class="getStatusClass(testCase.fbrResponse.validationResponse.status)"
                          >
                            {{ testCase.fbrResponse.validationResponse.status }}
                          </span>
                        </div>
                        <div
                          v-if="testCase.fbrResponse.validationResponse.errorCode"
                          class="detail-row"
                        >
                          <span class="detail-label">Error Code:</span>
                          <span class="detail-value text-danger">{{
                            testCase.fbrResponse.validationResponse.errorCode
                          }}</span>
                        </div>
                        <div
                          v-if="testCase.fbrResponse.validationResponse.error"
                          class="detail-row"
                        >
                          <span class="detail-label">Error:</span>
                          <span class="detail-value text-danger">{{
                            testCase.fbrResponse.validationResponse.error
                          }}</span>
                        </div>
                      </div>

                      <!-- Invoice Statuses for Success Cases -->
                      <div
                        v-if="
                          testCase.fbrResponse.validationResponse.invoiceStatuses &&
                          testCase.fbrResponse.validationResponse.invoiceStatuses.length > 0
                        "
                        class="invoice-statuses mt-3"
                      >
                        <h6 class="statuses-title">Invoice Statuses</h6>
                        <div class="statuses-list">
                          <div
                            v-for="(status, index) in testCase.fbrResponse.validationResponse
                              .invoiceStatuses"
                            :key="index"
                            class="status-item"
                          >
                            <div class="status-header">
                              <span class="item-sno">Item {{ status.itemSNo }}</span>
                              <span class="status-badge" :class="getStatusClass(status.status)">
                                {{ status.status }}
                              </span>
                            </div>
                            <div class="status-details">
                              <div class="detail-row">
                                <span class="detail-label">Invoice No:</span>
                                <span class="detail-value">{{ status.invoiceNo }}</span>
                              </div>
                              <div v-if="status.errorCode" class="detail-row">
                                <span class="detail-label">Error Code:</span>
                                <span class="detail-value text-danger">{{ status.errorCode }}</span>
                              </div>
                              <div v-if="status.error" class="detail-row">
                                <span class="detail-label">Error:</span>
                                <span class="detail-value text-danger">{{ status.error }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Error Details for Error Cases -->
                    <div v-if="testCase.fbrResponse.details" class="error-details mb-3">
                      <h6 class="error-title">Error Details</h6>
                      <div class="error-info">
                        <div class="detail-row">
                          <span class="detail-label">Date:</span>
                          <span class="detail-value">{{ testCase.fbrResponse.details.dated }}</span>
                        </div>
                        <div
                          v-if="testCase.fbrResponse.details.validationResponse"
                          class="detail-row"
                        >
                          <span class="detail-label">Validation Response:</span>
                          <span class="detail-value text-danger">{{
                            testCase.fbrResponse.details.validationResponse.error
                          }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Test Case IDs Summary -->
        <div
          v-if="testResults.data.testCaseIds && testResults.data.testCaseIds.length > 0"
          class="test-case-ids mt-4"
        >
          <h6 class="mb-2">Affected Test Case IDs:</h6>
          <div class="test-case-ids-list">
            <b-badge
              v-for="id in testResults.data.testCaseIds"
              :key="id"
              variant="secondary"
              class="mr-2 mb-2"
            >
              {{ id }}
            </b-badge>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-else-if="isRunningTest" class="test-loading">
        <div class="loading-container">
          <div class="loading-animation">
            <div class="loading-spinner">
              <div class="spinner-ring">
                <div class="spinner-ring-inner"></div>
              </div>
              <div class="spinner-dots">
                <div class="dot"></div>
                <div class="dot"></div>
                <div class="dot"></div>
              </div>
            </div>
            <div class="loading-content">
              <h4 class="loading-title">Running Test Cases</h4>
              <p class="loading-text">
                Please wait while we validate your business configuration...
              </p>
              <div class="loading-progress">
                <div class="progress-bar">
                  <div class="progress-fill"></div>
                </div>
                <span class="progress-text">Analyzing FBR integration</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <template #modal-footer>
        <div class="d-flex justify-content-between w-100">
          <div class="text-muted">
            <small v-if="testResults && testResults.serverTime">
              Server Time: {{ formatServerTime(testResults.serverTime) }}
            </small>
          </div>
          <b-button variant="secondary" @click="closeTestResultsModal"> Close </b-button>
        </div>
      </template>
    </b-modal>

    <!-- Custom Test Case Modal -->
    <b-modal
      v-model="showCustomTestModal"
      title="Custom Test Case Details"
      size="md"
      :hide-footer="false"
      class="custom-test-modal"
      scrollable
      :no-close-on-backdrop="isExecutingTest"
      :no-close-on-esc="isExecutingTest"
    >
      <div
        v-if="selectedBusinessForCustomTest && selectedBusinessForCustomTest.id"
        class="custom-test-content"
      >
        <!-- Test Case Selection Section -->
        <div class="test-case-selection-section">
          <h4>Select Test Case</h4>
          <p>Choose a specific test case to execute for this business</p>

          <div class="test-case-dropdown-container">
            <label class="form-label">Test Case ID:</label>
            <v-select
              v-model="selectedCustomTestCase"
              :options="testCaseOptions"
              placeholder="Choose a test case (SN001 - SN028)"
              class="custom-test-case-dropdown"
              :clearable="false"
              :searchable="true"
              label="label"
              :disabled="isExecutingTest"
              @open="forceDropdownPosition"
              @search:focus="forceDropdownPosition"
              @search:blur="forceDropdownPosition"
            />
            <small class="form-text"> Available test cases: SN001 through SN028 </small>
          </div>
        </div>

        <!-- Business Information Section - Enhanced -->
        <div class="business-info-enhanced">
          <div class="business-header">
            <div class="header-icon">
              <feather-icon icon="BriefcaseIcon" size="20" />
            </div>
            <div class="header-content">
              <h5 class="business-title">Business Information</h5>
              <p class="business-subtitle">Complete business details for testing</p>
            </div>
          </div>

          <div class="business-details-grid">
            <div class="detail-card">
              <div class="detail-icon">
                <feather-icon icon="UserIcon" size="18" />
              </div>
              <div class="detail-content">
                <div class="detail-label">CNIC</div>
                <div class="detail-value">{{ selectedBusinessForCustomTest.cnic || 'N/A' }}</div>
              </div>
            </div>

            <div class="detail-card">
              <div class="detail-icon">
                <feather-icon icon="MapPinIcon" size="18" />
              </div>
              <div class="detail-content">
                <div class="detail-label">Province</div>
                <div class="detail-value">
                  {{ selectedBusinessForCustomTest.province || 'N/A' }}
                </div>
              </div>
            </div>

            <div class="detail-card">
              <div class="detail-icon">
                <feather-icon icon="HomeIcon" size="18" />
              </div>
              <div class="detail-content">
                <div class="detail-label">City</div>
                <div class="detail-value">{{ selectedBusinessForCustomTest.address || 'N/A' }}</div>
              </div>
            </div>

            <div class="detail-card">
              <div class="detail-icon">
                <feather-icon icon="KeyIcon" size="18" />
              </div>
              <div class="detail-content">
                <div class="detail-label">Token</div>
                <div class="detail-value token-value">
                  {{ selectedBusinessForCustomTest.stagingToken || 'N/A' }}
                </div>
              </div>
            </div>
          </div>

          <div class="business-summary">
            <div class="summary-header">
              <feather-icon icon="BuildingIcon" size="16" />
              <span>Business Details</span>
            </div>
            <div class="summary-grid">
              <div class="summary-item">
                <div class="summary-label">ID</div>
                <div class="summary-value">{{ selectedBusinessForCustomTest.id }}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">Name</div>
                <div class="summary-value">
                  {{
                    selectedBusinessForCustomTest.businessName ||
                    selectedBusinessForCustomTest.name ||
                    'N/A'
                  }}
                </div>
              </div>
              <div class="summary-item">
                <div class="summary-label">NTN</div>
                <div class="summary-value">
                  {{
                    selectedBusinessForCustomTest.ntnNumber ||
                    selectedBusinessForCustomTest.ntn ||
                    'N/A'
                  }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Test Execution Status Section - Enhanced -->
        <div v-if="lastTestResult" class="test-result-enhanced">
          <div class="result-card" :class="`result-${lastTestResult.status}`">
            <!-- Header -->
            <div class="result-header">
              <div class="header-left">
                <div class="status-icon" :class="`icon-${lastTestResult.status}`">
                  <feather-icon :icon="getTestResultIcon(lastTestResult.status)" size="20" />
                </div>
                <div class="header-text">
                  <h4 class="result-title">Test Case Results</h4>
                  <p class="result-subtitle">{{ lastTestResult.message }}</p>
                </div>
              </div>
              <div class="status-badge" :class="`badge-${lastTestResult.status}`">
                {{ getTestResultStatusText(lastTestResult.status) }}
              </div>
            </div>

            <!-- Test Case Info -->
            <div class="test-case-info">
              <span class="test-case-label">Test Case:</span>
              <span class="test-case-value">{{ lastTestResult.testCaseId }}</span>
            </div>

            <!-- Success Details -->
            <div v-if="lastTestResult.status === 'success'" class="result-content success-content">
              <div v-if="lastTestResult.invoiceNumber" class="info-item">
                <feather-icon icon="FileTextIcon" size="16" class="info-icon" />
                <div class="info-content">
                  <span class="info-label">Invoice Number</span>
                  <span class="info-value success">{{ lastTestResult.invoiceNumber }}</span>
                </div>
              </div>
              <div v-if="lastTestResult.dated" class="info-item">
                <feather-icon icon="CalendarIcon" size="16" class="info-icon" />
                <div class="info-content">
                  <span class="info-label">Date</span>
                  <span class="info-value">{{ lastTestResult.dated }}</span>
                </div>
              </div>
              <div class="info-item">
                <feather-icon icon="ClockIcon" size="16" class="info-icon" />
                <div class="info-content">
                  <span class="info-label">Executed</span>
                  <span class="info-value">{{ formatTimestamp(lastTestResult.timestamp) }}</span>
                </div>
              </div>
            </div>

            <!-- Error Details -->
            <div v-else-if="lastTestResult.status === 'error'" class="result-content error-content">
              <!-- FBR API Error Details -->
              <div v-if="lastTestResult.fbrError" class="error-section">
                <div class="error-header">
                  <feather-icon icon="AlertTriangleIcon" size="16" class="error-icon" />
                  <span class="error-title">FBR API Error Details</span>
                </div>
                <div class="error-items">
                  <div class="error-item">
                    <span class="error-label">Status Code</span>
                    <span class="error-value status-code">{{
                      lastTestResult.fbrError.status || 'N/A'
                    }}</span>
                  </div>
                  <div class="error-item">
                    <span class="error-label">Error Code</span>
                    <span class="error-value error-code">{{
                      getFbrErrorCode(lastTestResult.fbrError)
                    }}</span>
                  </div>
                  <div class="error-item">
                    <span class="error-label">Error Message</span>
                    <span class="error-value error-message">{{
                      getFbrErrorMessage(lastTestResult.fbrError, lastTestResult.error)
                    }}</span>
                  </div>
                  <div
                    v-if="getFbrErrorDescription(lastTestResult.fbrError)"
                    class="error-item description-item"
                  >
                    <span class="error-label">Description</span>
                    <span class="error-value error-description">{{
                      getFbrErrorDescription(lastTestResult.fbrError)
                    }}</span>
                  </div>
                </div>
              </div>

              <!-- General Error -->
              <div v-else class="error-section">
                <div class="error-header">
                  <feather-icon icon="XCircleIcon" size="16" class="error-icon" />
                  <span class="error-title">Error Details</span>
                </div>
                <div class="error-items">
                  <div class="error-item">
                    <span class="error-label">Error</span>
                    <span class="error-value error-message">{{
                      lastTestResult.error || 'Unknown error occurred'
                    }}</span>
                  </div>
                </div>
              </div>

              <div class="info-item">
                <feather-icon icon="ClockIcon" size="16" class="info-icon" />
                <div class="info-content">
                  <span class="info-label">Executed</span>
                  <span class="info-value">{{ formatTimestamp(lastTestResult.timestamp) }}</span>
                </div>
              </div>
            </div>

            <!-- Other Status Details -->
            <div v-else class="result-content" style="padding: 0 1.5rem 1.5rem 1.5rem">
              <div
                v-if="lastTestResult.error"
                class="info-item"
                style="display: flex; align-items: flex-start; gap: 0.75rem; margin-bottom: 1rem"
              >
                <feather-icon
                  icon="InfoIcon"
                  size="16"
                  class="info-icon"
                  style="color: #6b7280; margin-top: 0.125rem; flex-shrink: 0"
                />
                <div
                  class="info-content"
                  style="flex: 1; display: flex; flex-direction: column; gap: 0.25rem"
                >
                  <span
                    class="info-label"
                    style="font-size: 0.875rem; font-weight: 600; color: #374151"
                    >Message</span
                  >
                  <span
                    class="info-value"
                    style="font-size: 0.875rem; color: #6b7280; word-break: break-word"
                    >{{ lastTestResult.error }}</span
                  >
                </div>
              </div>
              <div
                class="info-item"
                style="display: flex; align-items: flex-start; gap: 0.75rem; margin-bottom: 0"
              >
                <feather-icon
                  icon="ClockIcon"
                  size="16"
                  class="info-icon"
                  style="color: #6b7280; margin-top: 0.125rem; flex-shrink: 0"
                />
                <div
                  class="info-content"
                  style="flex: 1; display: flex; flex-direction: column; gap: 0.25rem"
                >
                  <span
                    class="info-label"
                    style="font-size: 0.875rem; font-weight: 600; color: #374151"
                    >Executed</span
                  >
                  <span
                    class="info-value"
                    style="font-size: 0.875rem; color: #6b7280; word-break: break-word"
                    >{{ formatTimestamp(lastTestResult.timestamp) }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else class="error-state text-center py-5">
        <div class="error-icon mb-3">
          <feather-icon icon="AlertCircleIcon" size="48" class="text-danger" />
        </div>
        <h5 class="text-danger mb-2">Business Data Not Available</h5>
        <p class="text-muted">Unable to load business information. Please try again.</p>
        <b-button variant="secondary" @click="closeCustomTestModal"> Close Modal </b-button>
      </div>

      <!-- Modal Footer - Simplified -->
      <template #modal-footer>
        <div class="d-flex justify-content-center w-100">
          <b-button
            variant="primary"
            @click="executeCustomTestCase"
            :disabled="
              !selectedCustomTestCase ||
              !selectedBusinessForCustomTest ||
              !selectedBusinessForCustomTest.id ||
              isExecutingTest
            "
            class="execute-btn-primary"
            size="lg"
          >
            <feather-icon v-if="!isExecutingTest" icon="PlayIcon" size="18" class="mr-2" />
            <b-spinner v-else small class="mr-2"></b-spinner>
            {{ isExecutingTest ? 'Executing Test Case...' : 'Execute Test Case' }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <!-- Update Password Modal -->
    <b-modal
      v-model="showUpdatePasswordModal"
      title="Update Password"
      size="md"
      :hide-footer="false"
      class="update-password-modal"
      @hidden="closeUpdatePasswordModal"
    >
      <div v-if="isLoadingUser" class="text-center py-3">
        <b-spinner small class="mr-2"></b-spinner>
        Loading user details...
      </div>

      <div v-else-if="userDetails">
        <div class="row mb-3">
          <div class="col-12">
            <div class="form-group">
              <label class="font-weight-bold">Business Name:</label>
              <p class="mb-0">{{ (selectedBusiness && selectedBusiness.businessName) || 'N/A' }}</p>
            </div>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-12">
            <div class="form-group">
              <label class="font-weight-bold">Username:</label>
              <p class="mb-0">{{ userDetails.username || 'N/A' }}</p>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <b-form-group label="New Password *" label-for="newPassword">
              <b-form-input
                id="newPassword"
                v-model="passwordForm.newPassword"
                type="password"
                :state="validateNewPassword"
                placeholder="Enter new password"
              />
              <b-form-invalid-feedback :state="validateNewPassword">
                Password must be at least 6 characters long
              </b-form-invalid-feedback>
            </b-form-group>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <b-form-group label="Confirm Password *" label-for="confirmNewPassword">
              <b-form-input
                id="confirmNewPassword"
                v-model="passwordForm.confirmPassword"
                type="password"
                :state="validateConfirmPassword"
                placeholder="Confirm new password"
              />
              <b-form-invalid-feedback :state="validateConfirmPassword">
                Passwords do not match
              </b-form-invalid-feedback>
            </b-form-group>
          </div>
        </div>
      </div>

      <div v-else class="text-center py-3">
        <p class="text-muted">No user details found for this business.</p>
      </div>

      <template #modal-footer>
        <b-button
          variant="secondary"
          @click="closeUpdatePasswordModal"
          :disabled="isUpdatingPassword"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="updatePassword"
          :disabled="!isPasswordFormValid || isUpdatingPassword"
        >
          <b-spinner small v-if="isUpdatingPassword" class="mr-2"></b-spinner>
          {{ isUpdatingPassword ? 'Updating...' : 'Update Password' }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import ActionCell from '@/components/common/list/ActionCell.vue';
import TimestampCell from '@/components/common/list/TimestampCell.vue';
import ResourceListHeader from '@/components/common/list/ResourceListHeader.vue';
import TableSkeleton from '@/components/common/skeletons/TableSkeleton.vue';
import { APIService, api } from '@/utils/api';
import { DATATABLE_SERIAL_COLUMNS, RESOURCES } from '@/utils/constants';
import StatisticCardHorizontal from '@core/components/statistics-cards/StatisticCardHorizontal.vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import AppBreadcrumb from '@core/layouts/components/AppBreadcrumb.vue';
import {
  BBadge,
  BBreadcrumb,
  BBreadcrumbItem,
  BButton,
  BCard,
  BCol,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormInvalidFeedback,
  BModal,
  BPagination,
  BSpinner,
  BRow,
  BTable,
  VBTooltip,
} from 'bootstrap-vue';
import {
  UserCheckIcon,
  UserMinusIcon,
  UsersIcon,
  SettingsIcon,
  InfoIcon,
  UserIcon,
  MapPinIcon,
  HomeIcon,
  KeyIcon,
  ClockIcon,
  XIcon,
  CheckCircleIcon,
  AlertCircleIcon,
} from 'vue-feather-icons';
import vSelect from 'vue-select';
import BusinessModal from './BusinessModal.vue';

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BRow,
    BCol,
    BBreadcrumb,
    BBreadcrumbItem,
    BCard,
    BButton,
    BFormInput,
    BFormInvalidFeedback,
    BSpinner,
    BTable,
    BBadge,
    vSelect,
    StatisticCardHorizontal,
    UsersIcon,
    UserCheckIcon,
    UserMinusIcon,
    SettingsIcon,
    InfoIcon,
    UserIcon,
    MapPinIcon,
    HomeIcon,
    KeyIcon,
    ClockIcon,
    XIcon,
    CheckCircleIcon,
    AlertCircleIcon,
    featherIcon: () => import('@core/components/feather-icon/FeatherIcon.vue'),
    BPagination,
    BFormSelect,
    AppBreadcrumb,
    TimestampCell,
    ActionCell,

    ResourceListHeader,
    TableSkeleton,
    BModal,
    BFormGroup,
    BFormCheckbox,
    BusinessModal,
  },
  data() {
    return {
      RESOURCES,
      isLoading: true,
      pageOptions: [
        { value: 20, text: '20' },
        { value: 40, text: '40' },
        { value: 60, text: '60' },
        { value: 100, text: '100' },
      ],

      pagination: {
        currentPage: 1,
        totalRows: 0,
      },

      filters: {
        currentPage: 1,
        perPage: 20,
        search: '',
      },

      // Table columns
      tableColumns: [
        ...DATATABLE_SERIAL_COLUMNS,
        { key: 'businessName', label: 'BUSINESS NAME', thStyle: { minWidth: '200px' } },
        { key: 'ntnNumber', label: 'NTN NUMBER', thStyle: { minWidth: '150px' } },
        { key: 'strn', label: 'STRN #', thStyle: { minWidth: '150px' } },
        { key: 'phone', label: 'PHONE', thStyle: { minWidth: '120px' } },
        { key: 'email', label: 'EMAIL', thStyle: { minWidth: '180px' } },
        { key: 'province', label: 'PROVINCE', thStyle: { minWidth: '120px' } },
        { key: 'runTest', label: 'RUN TEST', thStyle: { width: '120px' } },
        { key: 'customTest', label: 'CUSTOM TEST', thStyle: { width: '120px' } },
        { key: 'updatePassword', label: 'UPDATE PASSWORD', thStyle: { width: '140px' } },
        { key: 'actions', label: 'ACTIONS', thStyle: { width: '100px' } },
      ],

      businesses: [],

      isExporting: false,

      filterChangeTimeout: null,

      isEditing: false,
      business: {},
      viewDetail: false,
      isActionLoading: false, // Loading state for view/edit actions
      showTestResultsModal: false,
      testResults: null,
      isRunningTest: false,

      // Update Password Modal
      showUpdatePasswordModal: false,
      selectedBusiness: null,
      userDetails: null,
      isLoadingUser: false,
      isUpdatingPassword: false,
      passwordForm: {
        newPassword: '',
        confirmPassword: '',
      },

      // Test case options for custom testing
      testCaseOptions: [
        { label: 'SN001', value: 'SN001' },
        { label: 'SN002', value: 'SN002' },
        { label: 'SN003', value: 'SN003' },
        { label: 'SN004', value: 'SN004' },
        { label: 'SN005', value: 'SN005' },
        { label: 'SN006', value: 'SN006' },
        { label: 'SN007', value: 'SN007' },
        { label: 'SN008', value: 'SN008' },
        { label: 'SN009', value: 'SN009' },
        { label: 'SN010', value: 'SN010' },
        { label: 'SN011', value: 'SN011' },
        { label: 'SN012', value: 'SN012' },
        { label: 'SN013', value: 'SN013' },
        { label: 'SN014', value: 'SN014' },
        { label: 'SN015', value: 'SN015' },
        { label: 'SN016', value: 'SN016' },
        { label: 'SN017', value: 'SN017' },
        { label: 'SN018', value: 'SN018' },
        { label: 'SN019', value: 'SN019' },
        { label: 'SN020', value: 'SN020' },
        { label: 'SN021', value: 'SN021' },
        { label: 'SN022', value: 'SN022' },
        { label: 'SN023', value: 'SN023' },
        { label: 'SN024', value: 'SN024' },
        { label: 'SN025', value: 'SN025' },
        { label: 'SN026', value: 'SN026' },
        { label: 'SN027', value: 'SN027' },
        { label: 'SN028', value: 'SN028' },
      ],

      // Custom test modal state
      showCustomTestModal: false,
      selectedBusinessForCustomTest: null,
      selectedCustomTestCase: null,
      isExecutingTest: false,
      lastTestResult: null,

      // Video link for Watch Now button
      businessVideoLink: 'https://cdn.cloudious.net/file-1756976833659-991620426.mp4',
    };
  },
  computed: {
    businessID() {
      // Try to get from user module first
      if (this.$store.state.user && this.$store.state.user.businessID) {
        return this.$store.state.user.businessID;
      }
      // Then try root state
      if (this.$store.state.businessID) {
        return this.$store.state.businessID;
      }
      // Finally try userData
      if (this.$store.state.userData && this.$store.state.userData.businessID) {
        return this.$store.state.userData.businessID;
      }
      return 1; // Default for testing
    },
    userID() {
      // Try to get from user module first
      if (this.$store.state.user && this.$store.state.user.userID) {
        return this.$store.state.user.userID;
      }
      // Then try root state
      if (this.$store.state.userID) {
        return this.$store.state.userID;
      }
      // Finally try userData
      if (this.$store.state.userData && this.$store.state.userData.userID) {
        return this.$store.state.userData.userID;
      }
      return 1; // Default for testing
    },
    paginationInfo() {
      const totalRows = this.pagination.totalRows;
      const start = totalRows === 0 ? 0 : (this.filters.currentPage - 1) * this.filters.perPage + 1;
      const end = Math.min(start + this.filters.perPage - 1, totalRows);
      return `${start} to ${end} of ${totalRows} entries`;
    },
    validateNewPassword() {
      const password = (this.passwordForm.newPassword || '').toString().trim();
      return password.length >= 6; // Server requires minimum 6 characters
    },
    validateConfirmPassword() {
      const newPassword = (this.passwordForm.newPassword || '').toString().trim();
      const confirmPassword = (this.passwordForm.confirmPassword || '').toString().trim();

      if (!newPassword || !confirmPassword) return false;
      return newPassword === confirmPassword;
    },
    isPasswordFormValid() {
      return this.validateNewPassword && this.validateConfirmPassword;
    },
  },
  watch: {
    filters: {
      handler(newVal) {
        // Ensure currentPage is defined
        if (!newVal.currentPage) {
          console.warn('currentPage was reset to null in watcher, fixing to 1');
          this.$nextTick(() => {
            this.filters.currentPage = 1;
          });
        }

        // Ensure perPage is maintained
        if (!newVal.perPage) {
          this.filters.perPage = 20;
        }

        // Use a debounced approach to avoid excessive API calls
        if (this.filterChangeTimeout) {
          clearTimeout(this.filterChangeTimeout);
        }

        this.filterChangeTimeout = setTimeout(() => {
          this.fetchBusinesses();
        }, 300);
      },
      deep: true,
    },
    'filters.perPage': {
      handler(newVal) {
        if (!newVal) {
          console.warn('perPage was reset to null in watcher, fixing to 20');
          this.$nextTick(() => {
            this.filters.perPage = 20;
          });
        }
      },
      immediate: true,
    },
    showCustomTestModal(newVal, oldVal) {
      // Clear test result when modal closes
      if (oldVal === true && newVal === false) {
        this.lastTestResult = null;
        this.selectedCustomTestCase = null;
      }
    },
  },
  created() {
    this.filterChangeTimeout = null;
  },
  mounted() {
    this.fetchBusinesses();
  },
  methods: {
    updateSearchQuery(query) {
      const currentPerPage = this.filters.perPage || 20; // Keep current perPage or default to 20
      this.filters.search = query;
      this.filters.currentPage = 1; // Reset to first page on search
      this.filters.perPage = currentPerPage; // Maintain the perPage value

      // Force immediate fetch to avoid undefined issues with pagination
      if (this.filterChangeTimeout) {
        clearTimeout(this.filterChangeTimeout);
      }
      this.fetchBusinesses();
    },

    async fetchBusinesses() {
      if (!this.businessID) {
        console.error('Business ID is required to fetch businesses');
        return;
      }

      // Ensure perPage is always set
      if (!this.filters.perPage) {
        console.warn('perPage was reset to null, setting default value of 20');
        this.filters.perPage = 20;
      }

      // Ensure currentPage is always set
      if (!this.filters.currentPage) {
        console.warn('currentPage was reset to null, setting default value of 1');
        this.filters.currentPage = 1;
      }

      this.isLoading = true;

      try {
        // Use the specific Business API endpoint for pagination
        const response = await APIService.getByPagination(RESOURCES.BUSINESS, this.businessID, {
          pageNumber: this.filters.currentPage,
          pageSize: this.filters.perPage,
          search: this.filters.search || '',
        });

        // Extract the data based on the new API structure
        if (response && response.data) {
          const apiPayload = response.data; // This is the full API response object (status, data, message, serverTime)

          // Set the total rows count from the API response
          this.pagination.totalRows =
            (apiPayload.data && apiPayload.data.totalCount) ||
            (apiPayload.data && apiPayload.data.total) ||
            0;

          // Check if we have results in the expected format
          const results =
            (apiPayload.data && apiPayload.data.results) ||
            (apiPayload.data && apiPayload.data.data) ||
            [];

          if (Array.isArray(results)) {
            // Transform the results array
            this.businesses = this.formatBusinesses(results);
          } else {
            console.error('No results array found in API response', apiPayload);
            this.businesses = [];
          }
        } else {
          console.error('Invalid API response format:', response);
          this.businesses = [];
          this.pagination.totalRows = 0;
        }
      } catch (error) {
        console.error('Error fetching businesses:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Failed to fetch businesses',
          },
        });
        this.businesses = [];
        this.pagination.totalRows = 0;
      } finally {
        this.isLoading = false;
      }
    },

    formatBusinesses(businesses) {
      if (!Array.isArray(businesses)) {
        console.error('Businesses is not an array:', businesses);
        return [];
      }

      return businesses.map((business, index) => {
        return {
          serial: index + 1,
          id: business.id,
          businessName: business.businessName || business.name || '',
          ntnNumber: business.ntn || business.ntnNumber || '',
          strn: business.strn || business.gstNumber || business.gstNo || '',
          address: business.address || '',
          phone: business.phone || '',
          email: business.email || '',
          website: business.website || '',
          province: business.province || '',
          city: business.city || '',
          cnic: business.cnic || '',
          stagingToken: business.stagingToken || '',
          createdAt: business.createdDate
            ? {
                date: business.createdDate,
                time: null,
              }
            : null,
          updatedAt: business.updatedDate
            ? {
                date: business.updatedDate,
                time: null,
              }
            : null,
          metaAddedBy: business.metaAddedBy || '',
          metaUpdatedBy: business.metaUpdatedBy || '',
          // Add original business data for test case functionality
          originalBusiness: business,
          // Initialize selected test case for custom testing
          selectedTestCase: null,
        };
      });
    },

    async editBusiness(business) {
      try {
        this.isActionLoading = true;
        this.isEditing = true;
        this.viewDetail = false;

        // Use the pagination API to get the specific business by ID
        const response = await APIService.getByPagination(RESOURCES.BUSINESS, this.businessID, {
          pageNumber: 1,
          pageSize: 1000, // Get all businesses to find the specific one
          search: '',
        });

        if (response && response.data && response.data.data && response.data.data.results) {
          // Find the specific business by ID
          const businessData = response.data.data.results.find((b) => b.id === business.id);

          if (businessData) {
            this.business = businessData;
            this.$bvModal.show('modal-business');
          } else {
            throw new Error('Business not found');
          }
        } else {
          throw new Error('Failed to load business data');
        }
      } catch (error) {
        console.error('Error loading business for edit:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Failed to load business data for editing',
          },
        });
      } finally {
        this.isActionLoading = false;
      }
    },

    async viewBusiness(business) {
      try {
        this.isActionLoading = true;
        this.isEditing = true;
        this.viewDetail = true;

        // Use the pagination API to get the specific business by ID
        const response = await APIService.getByPagination(RESOURCES.BUSINESS, this.businessID, {
          pageNumber: 1,
          pageSize: 1000, // Get all businesses to find the specific one
          search: '',
        });

        if (response && response.data && response.data.data && response.data.data.results) {
          // Find the specific business by ID
          const businessData = response.data.data.results.find((b) => b.id === business.id);

          if (businessData) {
            this.business = businessData;
            this.$bvModal.show('modal-business');
          } else {
            throw new Error('Business not found');
          }
        } else {
          throw new Error('Failed to load business data');
        }
      } catch (error) {
        console.error('Error loading business for view:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Failed to load business data for viewing',
          },
        });
      } finally {
        this.isActionLoading = false;
      }
    },

    async exportData() {
      if (!this.businessID) {
        console.error('Business ID is required for export operation');
        return;
      }

      try {
        this.isExporting = true;

        const response = await api.get('/Business/Export', {
          params: {
            businessID: this.businessID,
            search: this.filters.search || '',
          },
        });

        if (response.data && response.data.status === 'success') {
          // Get the file path from the response
          const filePath = response.data.data.filePath;

          // Construct the full download URL
          const baseURL = process.env.VUE_APP_BASE_URL; // Replace with your actual base URL
          const downloadURL = `${baseURL}${filePath}`;

          // Create a link element and trigger download
          const link = document.createElement('a');
          link.href = downloadURL;
          link.download = filePath.split('/').pop(); // Get the filename from the path
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Export Successful',
              icon: 'CheckIcon',
              variant: 'success',
              text: response.data.message || 'Businesses data exported successfully',
            },
          });
        } else {
          throw new Error('Invalid response format from server');
        }
      } catch (error) {
        console.error('Error exporting businesses:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Export Failed',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text:
              (error.response && error.response.data && error.response.data.message) ||
              error.message ||
              'Failed to export businesses data',
          },
        });
      } finally {
        this.isExporting = false;
      }
    },

    handlePageChange(page) {
      console.log(`Moving to page ${page}`);
      // Update the current page in filters
      this.filters.currentPage = page;
      // Force fetch data for this page
      this.fetchBusinesses();
    },

    handlePerPageChange(value) {
      console.log(`Changing items per page to ${value}`);
      // Update perPage value and reset to first page
      this.filters.perPage = parseInt(value, 10);
      this.filters.currentPage = 1;
      // Force refresh data with new perPage value
      this.fetchBusinesses();
    },

    handleFilterChange(newFilters) {
      // Reset to page 1 when filters change
      this.filters.currentPage = 1;

      // Ensure perPage value is preserved when filters change
      if (!this.filters.perPage) {
        console.warn('perPage was reset during filter change, restoring to 20');
        this.filters.perPage = 20;
      }
    },

    async openModal() {
      try {
        this.isEditing = false;
        this.business = {};
        this.viewDetail = false;
        this.$bvModal.show('modal-business');
      } catch (error) {
        console.error('Error opening modal:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Failed to open modal',
          },
        });
      }
    },

    // Helper methods for test results modal
    closeTestResultsModal() {
      this.showTestResultsModal = false;
      this.testResults = null;
      this.isRunningTest = false;
    },

    getSummaryClass() {
      if (!this.testResults) return '';
      return this.testResults.status === 'success' ? 'summary-success' : 'summary-error';
    },

    getSummaryIcon() {
      if (!this.testResults) return 'InfoIcon';
      return this.testResults.status === 'success' ? 'CheckCircleIcon' : 'AlertCircleIcon';
    },

    getSummaryTitle() {
      if (!this.testResults) return '';
      return this.testResults.status === 'success' ? 'Test Cases Passed' : 'Test Cases Failed';
    },

    getTestCaseClass(status) {
      if (status === 'success') return 'test-case-success';
      if (status === 'Invalid') return 'test-case-error';
      return 'test-case-error';
    },

    getTestCaseBadgeVariant(status) {
      if (status === 'success') return 'success';
      if (status === 'Invalid') return 'danger';
      return 'danger';
    },

    getStatusClass(status) {
      if (status === 'success') return 'status-success';
      if (status === 'Invalid') return 'status-error';
      return 'status-error';
    },

    getStatusIcon(status) {
      if (status === 'success') return 'CheckCircleIcon';
      if (status === 'Invalid') return 'AlertCircleIcon';
      return 'AlertCircleIcon';
    },

    // New helper methods for enhanced test result display
    getTestResultIcon(status) {
      switch (status) {
        case 'success':
          return 'CheckCircleIcon';
        case 'error':
          return 'XCircleIcon';
        case 'warning':
          return 'AlertTriangleIcon';
        case 'info':
          return 'InfoIcon';
        default:
          return 'HelpCircleIcon';
      }
    },

    getTestResultStatusText(status) {
      switch (status) {
        case 'success':
          return 'PASSED';
        case 'error':
          return 'FAILED';
        case 'warning':
          return 'WARNING';
        case 'info':
          return 'INFO';
        default:
          return 'UNKNOWN';
      }
    },

    // Helper methods for FBR error display
    getFbrErrorCode(fbrError) {
      if (fbrError && fbrError.details) {
        if (fbrError.details.fault && fbrError.details.fault.code) {
          return fbrError.details.fault.code;
        }
        if (fbrError.details.validationResponse && fbrError.details.validationResponse.errorCode) {
          return fbrError.details.validationResponse.errorCode;
        }
      }
      return 'N/A';
    },

    getFbrErrorMessage(fbrError, fallbackError) {
      if (fbrError && fbrError.details) {
        if (fbrError.details.fault && fbrError.details.fault.message) {
          return fbrError.details.fault.message;
        }
        if (fbrError.details.validationResponse && fbrError.details.validationResponse.error) {
          return fbrError.details.validationResponse.error;
        }
      }
      return fallbackError || 'Unknown error';
    },

    getFbrErrorDescription(fbrError) {
      if (
        fbrError &&
        fbrError.details &&
        fbrError.details.fault &&
        fbrError.details.fault.description
      ) {
        return fbrError.details.fault.description;
      }
      return null;
    },

    getStatusText(status) {
      if (status === 'success') return 'PASSED';
      if (status === 'Invalid') return 'INVALID';
      return 'FAILED';
    },

    formatTimestamp(testCase) {
      // Generate a timestamp for demo purposes
      const now = new Date();
      return now.toLocaleTimeString();
    },

    formatFBRResponse(response) {
      if (!response) return 'No response data';
      try {
        return JSON.stringify(response, null, 2);
      } catch (error) {
        return String(response);
      }
    },

    viewTestDetails(testCase) {
      // Placeholder for viewing detailed test information
      console.log('Viewing test details:', testCase);
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Test Details',
          icon: 'InfoIcon',
          variant: 'info',
          text: `Viewing details for Test Case ${testCase.testCaseId}`,
        },
      });
    },

    retryTest(testCase) {
      // Placeholder for retrying a specific test
      console.log('Retrying test:', testCase);
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Retry Test',
          icon: 'RefreshCwIcon',
          variant: 'warning',
          text: `Retrying Test Case ${testCase.testCaseId}`,
        },
      });
    },

    formatServerTime(serverTime) {
      if (!serverTime) return '';
      try {
        const date = new Date(serverTime);
        return date.toLocaleString();
      } catch (error) {
        return serverTime;
      }
    },

    async addNewBusiness(businessData) {
      try {
        this.isLoading = true;

        // Prepare payload according to the API specification
        const payload = {
          business: {
            id: 0,
            businessID: this.businessID,
            name: businessData.businessName || '',
            logo: businessData.logo || '',
            address: businessData.address || '',
            phone: businessData.phone || '',
            email: businessData.email || '',
            website: businessData.website || '',
            stagingToken: businessData.stagingToken || '',
            liveToken: businessData.liveToken || '',
            integrationMode: businessData.integrationMode || '',
            createdDate: new Date().toISOString(),
            updatedDate: new Date().toISOString(),
            isDeleted: false,
            businessName: businessData.businessName || '',
            ntn: businessData.ntnNumber || '',
            cnic: businessData.cnic || '',
            province: businessData.province || '',
          },
          username: businessData.username || '',
          password: businessData.password || '',
        };

        // Use the specific API endpoint for initializing new business
        const response = await APIService.post('/api/Business/InitNewBusiness', payload);

        if (response && response.data && response.data.status === 'success') {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              icon: 'CheckIcon',
              variant: 'success',
              text: 'Business created successfully',
            },
          });

          this.$bvModal.hide('modal-business');
          this.fetchBusinesses(); // Refresh the list
        } else {
          throw new Error(
            (response && response.data && response.data.message) || 'Failed to create business'
          );
        }
      } catch (error) {
        console.error('Error creating business:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text:
              (error.response && error.response.data && error.response.data.message) ||
              error.message ||
              'Failed to create business',
          },
        });
      } finally {
        this.isLoading = false;
      }
    },

    async updateBusiness(businessData) {
      try {
        this.isLoading = true;

        // Prepare payload for updating business
        const payload = {
          id: businessData.id,
          businessID: this.businessID,
          name: businessData.businessName || businessData.name || '',
          logo: businessData.logo || '',
          address: businessData.address || '',
          phone: businessData.phone || '',
          email: businessData.email || '',
          website: businessData.website || '',
          stagingToken: businessData.stagingToken || '',
          liveToken: businessData.liveToken || '',
          integrationMode: businessData.integrationMode || '',
          businessName: businessData.businessName || businessData.name || '',
          ntn: businessData.ntnNumber || businessData.ntn || '',
          cnic: businessData.cnic || '',
          province: businessData.province || '',
        };

        // Use the Save API endpoint for updating business
        const response = await APIService.post('/api/Business/Save', payload, {
          params: { userID: this.userID },
        });

        if (response && response.data && response.data.status === 'success') {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              icon: 'CheckIcon',
              variant: 'success',
              text: 'Business updated successfully',
            },
          });

          this.$bvModal.hide('modal-business');
          this.fetchBusinesses(); // Refresh the list
        } else {
          throw new Error(
            (response && response.data && response.data.message) || 'Failed to update business'
          );
        }
      } catch (error) {
        console.error('Error updating business:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text:
              (error.response && error.response.data && error.response.data.message) ||
              error.message ||
              'Failed to update business',
          },
        });
      } finally {
        this.isLoading = false;
      }
    },

    async handleBusinessSaved() {
      // Reset the editing state
      this.isEditing = false;
      this.viewDetail = false;
      this.business = {};

      // Refresh the businesses list
      await this.fetchBusinesses();

      // Show success message
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Success',
          icon: 'CheckIcon',
          variant: 'success',
          text: 'Business saved successfully',
        },
      });
    },

    handleBusinessModalClose() {
      // Reset the state when modal is closed
      this.isEditing = false;
      this.viewDetail = false;
      this.business = {};
      this.isActionLoading = false;

      this.$bvModal.hide('modal-business');
    },

    // Update Password Methods
    async openUpdatePasswordModal(business) {
      this.selectedBusiness = business;
      this.showUpdatePasswordModal = true;
      this.isLoadingUser = true;
      this.userDetails = null;
      this.resetPasswordForm();

      try {
        // Debug: Log the business object to see available properties
        console.log('Business object:', business);

        // Use the business ID from the business object (try different possible property names)
        const businessID = business.id || business.businessID || business.businessId;
        console.log('Using businessID:', businessID);

        if (!businessID) {
          throw new Error('Business ID not found in business object');
        }

        // Call the API to get user details by business ID
        const response = await api.get('Business/LoadUserByBusiness', {
          params: { businessID: businessID },
        });

        if (response?.data?.status === 'success' && response?.data?.data) {
          this.userDetails = response.data.data;
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: 'Failed to load user details for this business',
            },
          });
        }
      } catch (error) {
        console.error('Error loading user details:', error);
        console.error('Error response:', error.response);
        console.error('Error response data:', error.response?.data);

        // Check if the error is related to missing token
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Failed to load user details';
        const errorString = JSON.stringify(error.response?.data || {});

        console.log('Error message:', errorMessage);
        console.log('Error string:', errorString);

        // Check for various token-related error patterns
        if (
          errorMessage.includes('Token is required') ||
          errorMessage.includes('Token') ||
          errorString.includes('Token is required') ||
          errorString.includes('"error":"Token is required"') ||
          errorMessage.toLowerCase().includes('token')
        ) {
          console.log('Token error detected, showing warning toast');
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Token Required',
              icon: 'KeyIcon',
              variant: 'warning',
              text: 'Please set the integration tokens (Staging Token and Live Token) for this business before accessing customer details. Go to Business Management to update the tokens.',
            },
          });
        } else {
          console.log('Non-token error, showing error toast');
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: errorMessage,
            },
          });
        }
      } finally {
        this.isLoadingUser = false;
      }
    },

    closeUpdatePasswordModal() {
      this.showUpdatePasswordModal = false;
      this.selectedBusiness = null;
      this.userDetails = null;
      this.resetPasswordForm();
    },

    resetPasswordForm() {
      this.passwordForm = {
        newPassword: '',
        confirmPassword: '',
      };
    },

    async updatePassword() {
      if (!this.isPasswordFormValid) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Validation Error',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
            text: 'Password must be at least 6 characters long and passwords must match',
          },
        });
        return;
      }

      this.isUpdatingPassword = true;

      try {
        // Prepare payload for password update
        const businessID =
          this.selectedBusiness.id ||
          this.selectedBusiness.businessID ||
          this.selectedBusiness.businessId;
        const payload = {
          businessID: businessID,
          password: this.passwordForm.newPassword,
        };

        // Call the password update API using PUT method with userID in headers and params
        const response = await api.put('Business/UpdatePassword', payload, {
          headers: {
            userID: this.userID,
          },
          params: {
            userID: this.userID,
          },
        });

        if (response?.data?.status === 'success' || response?.data) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              icon: 'CheckIcon',
              variant: 'success',
              text: 'Password updated successfully',
            },
          });

          this.closeUpdatePasswordModal();
        } else {
          throw new Error(response?.data?.message || 'Failed to update password');
        }
      } catch (error) {
        console.error('Error updating password:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error.response?.data?.message || error.message || 'Failed to update password',
          },
        });
      } finally {
        this.isUpdatingPassword = false;
      }
    },

    async runTestCase(business) {
      try {
        console.log('Running test case for business:', business);

        // Set loading state and show modal
        this.isRunningTest = true;
        this.showTestResultsModal = true;
        this.testResults = null;

        // Call the actual API endpoint for running test cases
        const response = await APIService.post(
          `SalesInvoiceFBR/RunTestCases/${business.id}`,
          {}, // Empty body as per API specification
          {
            params: { userID: this.userID },
          }
        );

        // Store the results and show them in the modal
        this.testResults = response.data;
        this.isRunningTest = false;

        // Show toast notification based on results
        if (response && response.data && response.data.status === 'success') {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Test Cases Completed',
              icon: 'CheckIcon',
              variant: 'success',
              text: `Test cases completed successfully for ${business.businessName || business.name}`,
            },
          });
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Test Cases Completed',
              icon: 'AlertCircleIcon',
              variant: 'warning',
              text: `Test cases completed with issues for ${business.businessName || business.name}`,
            },
          });
        }
      } catch (error) {
        console.error('Error running test case:', error);
        this.isRunningTest = false;

        // Show error in modal
        this.testResults = {
          status: 'error',
          message:
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            'Failed to run test cases',
          data: {
            validationResults: [],
            testCaseIds: [],
          },
        };

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Test Case Failed',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text:
              (error.response && error.response.data && error.response.data.message) ||
              error.message ||
              `Failed to run test cases for ${business.businessName || business.name}`,
          },
        });
      }
    },

    async runCustomTestCase(business) {
      try {
        console.log('=== runCustomTestCase called ===');
        console.log('Business parameter:', business);
        console.log('Business ID:', business?.id);
        console.log('Business properties:', {
          id: business?.id,
          businessName: business?.businessName,
          name: business?.name,
          cnic: business?.cnic,
          province: business?.province,
          city: business?.city,
          stagingToken: business?.stagingToken,
        });

        // Validate business data before proceeding
        if (!business || !business.id) {
          console.log('Validation failed: business data is invalid');
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              icon: 'AlertCircleIcon',
              variant: 'danger',
              text: 'Invalid business data. Please try again.',
            },
          });
          return;
        }

        // Clear previous test result when opening modal for different business
        this.lastTestResult = null;
        this.selectedCustomTestCase = null;

        // Set the selected business for custom test
        this.selectedBusinessForCustomTest = { ...business }; // Create a copy to avoid reference issues

        console.log('=== Modal data set ===');
        console.log('selectedBusinessForCustomTest set to:', this.selectedBusinessForCustomTest);
        console.log('selectedBusinessForCustomTest.id:', this.selectedBusinessForCustomTest.id);

        // Show the custom test modal
        this.showCustomTestModal = true;

        console.log('Modal opened successfully');
      } catch (error) {
        console.error('Error opening custom test modal:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Failed to open custom test modal',
          },
        });
      }
    },

    async executeCustomTestCase() {
      console.log('=== executeCustomTestCase called ===');
      console.log('selectedCustomTestCase:', this.selectedCustomTestCase);
      console.log('selectedBusinessForCustomTest:', this.selectedBusinessForCustomTest);

      if (!this.selectedCustomTestCase || !this.selectedBusinessForCustomTest) {
        console.log('Validation failed: missing test case or business data');
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Please select a test case and business',
          },
        });
        return;
      }

      // Additional validation to ensure business data exists
      if (!this.selectedBusinessForCustomTest.id) {
        console.log('Validation failed: business ID is missing');
        console.log('Business object:', this.selectedBusinessForCustomTest);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: 'Invalid business data. Please try again.',
          },
        });
        return;
      }

      try {
        console.log('=== Starting API execution ===');
        console.log(
          'Executing custom test case:',
          this.selectedCustomTestCase,
          'for business:',
          this.selectedBusinessForCustomTest
        );

        // Set execution state
        this.isExecutingTest = true;

        // Store business data locally
        const business = { ...this.selectedBusinessForCustomTest };
        const selectedTestCase = this.selectedCustomTestCase;

        console.log('Local business copy:', business);
        console.log('Local selectedTestCase:', selectedTestCase);
        console.log('selectedTestCase type:', typeof selectedTestCase);
        console.log('selectedTestCase value:', selectedTestCase?.value);
        console.log('selectedTestCase label:', selectedTestCase?.label);

        // Prepare payload according to the API specification with safe property access
        const businessCnic = business.cnic || '';
        const businessNtn = business.ntn || business.ntnNumber || '';

        // Use CNIC if available, otherwise use NTN in the cnic field
        const cnicOrNtn = businessCnic && businessCnic.trim() !== '' ? businessCnic : businessNtn;

        const payload = {
          businessID: business.id || 0,
          cnic: cnicOrNtn,
          province: business.province || '',
          city: business.address || '', // Use address instead of city
          token: business.stagingToken || '',
          case: selectedTestCase.value || selectedTestCase, // Extract value if it's an object, otherwise use as is
        };

        // Log which value is being used
        if (businessCnic && businessCnic.trim() !== '') {
          console.log('Using CNIC in cnic field:', businessCnic);
        } else {
          console.log('CNIC is empty, using NTN in cnic field:', businessNtn);
        }

        console.log('=== API Payload Details ===');
        console.log('API Payload:', payload);
        console.log('Payload JSON:', JSON.stringify(payload, null, 2));
        console.log('Case field value:', payload.case);
        console.log('Case field type:', typeof payload.case);
        console.log('Business data being used:', business);

        // Log the equivalent cURL command for debugging
        const curlCommand = `curl --location 'https://ibico-fbrr.cloudious.net/custom' \\
--header 'Content-Type: application/json' \\
--data '${JSON.stringify(payload, null, 2)}'`;
        console.log('Equivalent cURL command:');
        console.log(curlCommand);

        // Call the external API endpoint for custom test case
        const response = await APIService.post('https://ibico-fbrr.cloudious.net/custom', payload, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        console.log('API Response:', response);

        // Parse FBR API response and show appropriate message
        if (response && response.data) {
          const fbrResponse = response.data;

          // Check for validationResponse with Invalid status first
          if (
            fbrResponse.validationResponse &&
            fbrResponse.validationResponse.status === 'Invalid'
          ) {
            // Handle FBR API validation error response
            const errorMessage = fbrResponse.validationResponse.error || 'Validation failed';
            const errorCode = fbrResponse.validationResponse.statusCode || 'N/A';

            // Get detailed error from invoiceStatuses if available
            let detailedError = errorMessage;
            if (
              fbrResponse.validationResponse.invoiceStatuses &&
              fbrResponse.validationResponse.invoiceStatuses.length > 0
            ) {
              const firstError = fbrResponse.validationResponse.invoiceStatuses[0];
              detailedError = firstError.error || errorMessage;
              const itemErrorCode = firstError.errorCode || errorCode;
              detailedError = `${detailedError} (Code: ${itemErrorCode})`;
            }

            const toastMessage = `Test case ${this.selectedCustomTestCase.value || this.selectedCustomTestCase} failed: ${detailedError}`;

            this.$bvToast.toast(toastMessage, {
              title: 'Test Case Failed',
              variant: 'danger',
              solid: true,
              autoHideDelay: 10000,
              appendToast: true,
            });

            // Store detailed result in modal for display
            this.lastTestResult = {
              status: 'error',
              message: `Test case ${this.selectedCustomTestCase.value || this.selectedCustomTestCase} failed`,
              testCaseId: this.selectedCustomTestCase.value || this.selectedCustomTestCase,
              error: detailedError,
              fbrResponse: fbrResponse,
              timestamp: new Date(),
            };
          } else if (fbrResponse.error) {
            // Handle FBR API error response with detailed error parsing
            const errorMessage =
              fbrResponse.details?.fault?.message || fbrResponse.error || 'Unknown error';
            const errorDescription = fbrResponse.details?.fault?.description || '';

            // Show detailed error in toast
            const errorCode = fbrResponse.details?.fault?.code || 'N/A';
            const toastMessage = `Test case ${this.selectedCustomTestCase.value || this.selectedCustomTestCase} failed: ${errorMessage} (Code: ${errorCode})`;

            this.$bvToast.toast(toastMessage, {
              title: 'Test Case Failed',
              variant: 'danger',
              solid: true,
              autoHideDelay: 10000,
              appendToast: true,
            });

            // Store detailed result in modal for display
            this.lastTestResult = {
              status: 'error',
              message: `Test case ${this.selectedCustomTestCase.value || this.selectedCustomTestCase} failed`,
              testCaseId: this.selectedCustomTestCase.value || this.selectedCustomTestCase,
              error: errorMessage,
              fbrError: {
                status: fbrResponse.status,
                details: fbrResponse.details,
              },
              timestamp: new Date(),
            };
          } else if (fbrResponse.invoiceNumber) {
            // Handle FBR API success response
            this.$bvToast.toast(
              `Test case ${this.selectedCustomTestCase.value || this.selectedCustomTestCase} executed successfully. Invoice: ${fbrResponse.invoiceNumber}`,
              {
                title: 'Test Case Passed',
                variant: 'success',
                solid: true,
                autoHideDelay: 5000,
                appendToast: true,
              }
            );

            // Store result in modal for display
            this.lastTestResult = {
              status: 'success',
              message: `Test case ${this.selectedCustomTestCase.value || this.selectedCustomTestCase} executed successfully`,
              testCaseId: this.selectedCustomTestCase.value || this.selectedCustomTestCase,
              invoiceNumber: fbrResponse.invoiceNumber,
              timestamp: new Date(),
            };
          } else {
            // Handle other response formats
            this.$bvToast.toast(
              `Test case ${this.selectedCustomTestCase.value || this.selectedCustomTestCase} completed for ${business.businessName || business.name || 'Business'}`,
              {
                title: 'Test Case Completed',
                variant: 'info',
                solid: true,
                autoHideDelay: 5000,
                appendToast: true,
              }
            );

            this.lastTestResult = {
              status: 'info',
              message: `Test case ${this.selectedCustomTestCase.value || this.selectedCustomTestCase} completed`,
              testCaseId: this.selectedCustomTestCase.value || this.selectedCustomTestCase,
              timestamp: new Date(),
            };
          }
        } else {
          // Handle empty or invalid response
          this.$bvToast.toast(
            `Test case ${this.selectedCustomTestCase.value || this.selectedCustomTestCase} completed with no response data`,
            {
              title: 'Test Case Completed',
              variant: 'warning',
              solid: true,
              autoHideDelay: 5000,
              appendToast: true,
            }
          );

          this.lastTestResult = {
            status: 'warning',
            message: 'No response data received',
            testCaseId: this.selectedCustomTestCase.value || this.selectedCustomTestCase,
            timestamp: new Date(),
          };
        }
      } catch (error) {
        console.error('=== Error in executeCustomTestCase ===');
        console.error('Error executing custom test case:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response,
          stack: error.stack,
        });

        // Parse error response for FBR API errors
        let errorMessage = error.message || 'Unknown error';
        let fbrError = null;

        if (error.response && error.response.data) {
          const errorData = error.response.data;

          // Check if it's an FBR API error response
          if (errorData.error === 'FBR API returned error' && errorData.details) {
            fbrError = {
              status: errorData.status,
              details: errorData.details,
            };
            let validationError = errorData.details.validationResponse?.error;
            errorMessage =
              errorData.details.fault?.message ||
              validationError ||
              errorData.error ||
              errorMessage;
          } else {
            errorMessage = errorData.message || errorData.error || errorMessage;
          }
        }

        // Show detailed error toast
        const errorCode = fbrError
          ? fbrError.details?.fault?.code ||
            fbrError.details?.validationResponse?.errorCode ||
            'N/A'
          : 'N/A';
        const toastMessage = fbrError
          ? `Test case ${this.selectedCustomTestCase.value || this.selectedCustomTestCase} failed: ${errorMessage} (Code: ${errorCode})`
          : `Test case ${this.selectedCustomTestCase.value || this.selectedCustomTestCase} failed: ${errorMessage}`;

        this.$bvToast.toast(toastMessage, {
          title: 'Test Case Failed',
          variant: 'danger',
          solid: true,
          autoHideDelay: 10000,
          appendToast: true,
        });

        // Store error result in modal
        this.lastTestResult = {
          status: 'error',
          message: `Test case ${this.selectedCustomTestCase.value || this.selectedCustomTestCase} failed`,
          testCaseId: this.selectedCustomTestCase.value || this.selectedCustomTestCase,
          error: errorMessage,
          fbrError: fbrError,
          timestamp: new Date(),
        };
      } finally {
        // Reset execution state
        this.isExecutingTest = false;
      }
    },

    closeCustomTestModal() {
      this.showCustomTestModal = false;
      this.selectedBusinessForCustomTest = null;
      this.selectedCustomTestCase = null;
      this.lastTestResult = null;
      this.isExecutingTest = false;
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return 'N/A';
      try {
        return new Date(timestamp).toLocaleString();
      } catch (error) {
        return 'Invalid Date';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.pagination-select {
  min-width: 80px;
  background-color: #f8f8f8;
  border-color: #ddd;
}

.update-password-btn {
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.875rem;
  padding: 0.375rem 0.75rem;
  transition: all 0.2s ease;
  border-width: 1.5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  min-width: auto;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
  }

  &:active {
    transform: translateY(0);
  }

  .feather-icon {
    margin-right: 0.375rem;
    flex-shrink: 0;
    display: inline-block;
    vertical-align: middle;
  }

  // Ensure text and icon are on same line
  span,
  .feather-icon {
    display: inline-block;
    vertical-align: middle;
    line-height: 1;
  }
}

.pagination-modern {
  .page-item {
    margin: 0 0.2rem;

    .page-link {
      border-radius: 0.358rem;
      border: none;
      min-width: 36px;
      height: 36px;
      line-height: 36px;
      padding: 0;
      text-align: center;
      font-weight: 500;
      background-color: #f8f8f8;
      color: #6e6b7b;

      &:hover {
        background-color: #7367f0;
        color: #fff;
      }
    }

    &.active .page-link {
      background-color: #7367f0;
      color: #fff;
    }
  }
}

.scrollContent {
  max-height: 50vh;
  overflow-y: scroll;
}

.action-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 8px;
}

.loading-content {
  text-align: center;
}

.loading-text {
  color: #6e6b7b;
  font-size: 14px;
  margin: 0;
}

.run-test-btn {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;
  border-width: 2px;
  background: linear-gradient(135deg, #7367f0 0%, #5e50ee 100%);
  border-color: #7367f0;
  color: white;
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    // No hover effects - maintain same appearance
    color: white !important;
    background: linear-gradient(135deg, #7367f0 0%, #5e50ee 100%) !important;
    border-color: #7367f0 !important;
  }

  &:active:not(:disabled) {
    // No active effects - maintain same appearance
    color: white !important;
    background: linear-gradient(135deg, #7367f0 0%, #5e50ee 100%) !important;
    border-color: #7367f0 !important;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  .feather-icon {
    transition: transform 0.3s ease;
    flex-shrink: 0;
  }

  // No hover effects on icons
}

// Custom Test Column Styles
.custom-test-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.test-case-dropdown {
  min-width: 120px;
  font-size: 0.875rem;

  .custom-select {
    border-radius: 8px;
    border-color: #d8d6de;

    &:focus {
      border-color: #7367f0;
      box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.25);
    }
  }
}

.custom-test-btn {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;
  border-width: 2px;
  min-width: 120px;
  background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
  border-color: #17a2b8;
  color: white;
  box-shadow: 0 2px 8px rgba(23, 162, 184, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    // No hover effects - maintain same appearance
    color: white !important;
    background: linear-gradient(135deg, #17a2b8 0%, #138496 100%) !important;
    border-color: #17a2b8 !important;
  }

  &:active:not(:disabled) {
    // No active effects - maintain same appearance
    color: white !important;
    background: linear-gradient(135deg, #17a2b8 0%, #138496 100%) !important;
    border-color: #17a2b8 !important;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  .feather-icon {
    transition: transform 0.3s ease;
    flex-shrink: 0;
  }

  // No hover effects on icons
}

// Custom Test Modal Styles - Enhanced with !important for override
.custom-test-modal.modal {
  .modal-dialog {
    max-width: 520px !important;
    width: 90% !important;
  }

  .modal-content {
    border: none !important;
    border-radius: 20px !important;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
    overflow: hidden;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
  }

  .modal-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
    color: white !important;
    border: none !important;
    padding: 1.5rem 2rem !important;
    position: relative;
    text-align: center;
    min-height: 80px !important;
    display: flex;
    align-items: center;
    justify-content: center;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="white" opacity="0.1"/><circle cx="75" cy="75" r="1" fill="white" opacity="0.1"/><circle cx="50" cy="10" r="0.5" fill="white" opacity="0.1"/><circle cx="10" cy="60" r="0.5" fill="white" opacity="0.1"/><circle cx="90" cy="40" r="0.5" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
      opacity: 0.4;
    }

    .modal-title {
      font-weight: 800;
      font-size: 2rem;
      position: relative;
      z-index: 1;
      margin: 0 auto;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      letter-spacing: -0.5px;
    }

    .close {
      color: white;
      opacity: 0.9;
      font-size: 1.75rem;
      position: absolute;
      right: 2rem;
      top: 50%;
      transform: translateY(-50%);
      z-index: 1;
      transition: all 0.3s ease;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(10px);

      &:hover {
        opacity: 1;
        transform: translateY(-50%) scale(1.1);
        background: rgba(255, 255, 255, 0.2);
      }
    }
  }

  .modal-body {
    padding: 1.25rem !important;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.2), transparent);
    }
  }

  .custom-test-content {
    .test-case-selection-section {
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95) 0%,
        rgba(248, 250, 252, 0.95) 100%
      );
      border-radius: 16px;
      padding: 1.25rem;
      border: 1px solid rgba(102, 126, 234, 0.08);
      backdrop-filter: blur(20px);
      box-shadow: 0 8px 28px rgba(0, 0, 0, 0.06);
      margin-bottom: 1rem;
      position: relative;
      overflow: hidden;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: linear-gradient(90deg, #667eea, #764ba2, #667eea);
      }

      .section-header {
        text-align: center;
        margin-bottom: 1rem;

        .test-case-icon-wrapper {
          display: flex;
          justify-content: center;
          margin-bottom: 0.75rem;

          .test-case-icon {
            width: 44px;
            height: 44px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            box-shadow: 0 8px 20px rgba(102, 126, 234, 0.25);
            position: relative;

            &::after {
              content: '';
              position: absolute;
              top: -4px;
              left: -4px;
              right: -4px;
              bottom: -4px;
              background: linear-gradient(135deg, #667eea, #764ba2);
              border-radius: 50%;
              z-index: -1;
              opacity: 0.3;
              animation: pulse-ring 2s ease-in-out infinite;
            }

            @keyframes pulse {
              0%,
              100% {
                transform: scale(1);
              }
              50% {
                transform: scale(1.05);
              }
            }

            @keyframes pulse-ring {
              0%,
              100% {
                transform: scale(1);
                opacity: 0.3;
              }
              50% {
                transform: scale(1.1);
                opacity: 0.1;
              }
            }
          }
        }

        .section-title {
          color: #1e293b;
          font-weight: 800;
          font-size: 1.125rem;
          margin-bottom: 0.25rem;
          letter-spacing: -0.5px;
        }

        .section-subtitle {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
          margin: 0;
          line-height: 1.6;
        }
      }

      .test-case-dropdown-container {
        max-width: 320px;
        margin: 0 auto;

        .form-label {
          font-weight: 700;
          color: #334155;
          margin-bottom: 0.5rem;
          display: block;
          font-size: 1rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .custom-test-case-dropdown {
          // Enhanced v-select styling
          .vs__dropdown-toggle {
            border: 2px solid rgba(102, 126, 234, 0.2);
            border-radius: 12px;
            padding: 0.6rem 0.9rem;
            background: rgba(255, 255, 255, 0.9);
            transition: all 0.3s ease;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);

            &:hover {
              border-color: rgba(102, 126, 234, 0.4);
              box-shadow: 0 6px 24px rgba(102, 126, 234, 0.1);
            }

            &:focus-within {
              border-color: #667eea;
              box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
            }
          }

          .vs__selected-options {
            padding: 0;
          }

          .vs__actions {
            padding: 0;
          }

          .vs__dropdown-menu {
            border: 1px solid rgba(102, 126, 234, 0.1);
            border-radius: 12px;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
            background: white;
            margin-top: 4px;
          }

          .vs__dropdown-option {
            padding: 0.75rem 1.25rem;
            color: #334155;
            font-weight: 500;
            transition: all 0.2s ease;

            &:hover {
              background: rgba(102, 126, 234, 0.05);
              color: #667eea;
            }

            &.vs__dropdown-option--highlight {
              background: rgba(102, 126, 234, 0.1);
              color: #667eea;
            }
          }
        }

        .form-text {
          color: #94a3b8;
          font-size: 0.875rem;
          font-style: italic;
          margin-top: 1rem;
          text-align: center;
          line-height: 1.5;
        }
      }
    }

    .business-details-section {
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95) 0%,
        rgba(248, 250, 252, 0.95) 100%
      );
      border-radius: 20px;
      padding: 3rem;
      border: 1px solid rgba(102, 126, 234, 0.08);
      backdrop-filter: blur(20px);
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.06);
      position: relative;
      overflow: hidden;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: linear-gradient(90deg, #10b981, #059669, #10b981);
      }

      .section-header {
        display: flex;
        align-items: center;
        margin-bottom: 3rem;
        padding: 2rem;
        background: linear-gradient(
          135deg,
          rgba(16, 185, 129, 0.08) 0%,
          rgba(5, 150, 105, 0.04) 100%
        );
        border-radius: 16px;
        border: 1px solid rgba(16, 185, 129, 0.1);
        position: relative;
        overflow: hidden;

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="dots" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="1" fill="%2310b981" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23dots)"/></svg>');
          opacity: 0.3;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1.25rem;
          position: relative;
          z-index: 1;

          .header-icon {
            width: 56px;
            height: 56px;
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            box-shadow: 0 8px 24px rgba(16, 185, 129, 0.3);
            position: relative;
            flex-shrink: 0;

            &::after {
              content: '';
              position: absolute;
              top: -3px;
              left: -3px;
              right: -3px;
              bottom: -3px;
              background: linear-gradient(135deg, #10b981, #059669);
              border-radius: 50%;
              z-index: -1;
              opacity: 0.2;
            }
          }

          .header-text {
            .section-title {
              color: #1e293b;
              font-weight: 800;
              font-size: 1.5rem;
              margin: 0;
              border: none;
              padding: 0;
              letter-spacing: -0.5px;
              line-height: 1.2;
            }

            .section-subtitle {
              color: #64748b;
              font-size: 1rem;
              font-weight: 500;
              margin: 0;
              margin-top: 0.5rem;
              line-height: 1.5;
            }
          }
        }
      }

      .business-details-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
        gap: 2rem;
        margin-bottom: 2.5rem;

        .detail-item {
          background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.95) 0%,
            rgba(248, 250, 252, 0.95) 100%
          );
          border-radius: 18px;
          padding: 2rem;
          border: 1px solid rgba(102, 126, 234, 0.08);
          backdrop-filter: blur(10px);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;

          &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 4px;
            height: 100%;
            background: linear-gradient(180deg, #667eea, #764ba2);
            opacity: 0;
            transition: opacity 0.3s ease;
          }

          &:hover {
            transform: translateY(-6px);
            border-color: rgba(102, 126, 234, 0.15);
            box-shadow: 0 16px 48px rgba(0, 0, 0, 0.08);

            &::before {
              opacity: 1;
            }
          }

          &.primary {
            border-left: 4px solid #667eea;
            background: linear-gradient(
              135deg,
              rgba(102, 126, 234, 0.03) 0%,
              rgba(255, 255, 255, 0.95) 100%
            );

            &::before {
              background: linear-gradient(180deg, #667eea, #764ba2);
              opacity: 1;
            }
          }

          .detail-header {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-bottom: 1.5rem;

            .detail-icon {
              color: #667eea;
              background: rgba(102, 126, 234, 0.12);
              padding: 0.75rem;
              border-radius: 12px;
              transition: all 0.3s ease;
              flex-shrink: 0;
              width: 48px;
              height: 48px;
              display: flex;
              align-items: center;
              justify-content: center;
            }

            .detail-label {
              font-weight: 800;
              color: #334155;
              font-size: 1rem;
              text-transform: uppercase;
              letter-spacing: 1px;
              margin: 0;
              line-height: 1.2;
            }
          }

          .detail-value {
            font-weight: 600;
            color: #1e293b;
            font-size: 1.25rem;
            padding: 1.25rem;
            background: rgba(255, 255, 255, 0.9);
            border-radius: 12px;
            border: 1px solid rgba(102, 126, 234, 0.08);
            min-height: 56px;
            display: flex;
            align-items: center;
            transition: all 0.3s ease;
            position: relative;
            line-height: 1.4;

            &::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: linear-gradient(
                135deg,
                rgba(102, 126, 234, 0.03) 0%,
                rgba(255, 255, 255, 0.9) 100%
              );
              border-radius: 12px;
              opacity: 0;
              transition: opacity 0.3s ease;
            }

            &:hover {
              border-color: rgba(102, 126, 234, 0.15);
              background: rgba(255, 255, 255, 0.98);
              transform: translateY(-2px);

              &::before {
                opacity: 1;
              }
            }

            &.token-value {
              font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
              font-size: 1rem;
              word-break: break-all;
              background: rgba(102, 126, 234, 0.06);
              border-color: rgba(102, 126, 234, 0.15);
              color: #667eea;
              font-weight: 500;
              line-height: 1.5;
            }
          }
        }
      }

      .business-summary-card {
        background: linear-gradient(
          135deg,
          rgba(102, 126, 234, 0.04) 0%,
          rgba(118, 75, 162, 0.02) 100%
        );
        border-radius: 18px;
        padding: 2.5rem;
        border: 1px solid rgba(102, 126, 234, 0.08);
        position: relative;
        overflow: hidden;

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M 20 0 L 0 0 0 20" fill="none" stroke="%23667eea" stroke-width="0.5" opacity="0.08"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
        }

        .summary-content {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 1.5rem;
          position: relative;
          z-index: 1;

          .summary-item {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;

            .summary-label {
              font-weight: 800;
              color: #64748b;
              font-size: 0.875rem;
              text-transform: uppercase;
              letter-spacing: 1px;
              line-height: 1.2;
            }

            .summary-value {
              font-weight: 700;
              color: #1e293b;
              font-size: 1.125rem;
              padding: 1rem;
              background: rgba(255, 255, 255, 0.9);
              border-radius: 10px;
              border: 1px solid rgba(102, 126, 234, 0.08);
              transition: all 0.3s ease;
              line-height: 1.3;

              &:hover {
                background: rgba(255, 255, 255, 0.98);
                border-color: rgba(102, 126, 234, 0.15);
                transform: translateY(-2px);
                box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
              }
            }
          }
        }
      }
    }

    .test-execution-status-section {
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95) 0%,
        rgba(248, 250, 252, 0.95) 100%
      );
      border-radius: 20px;
      padding: 2.5rem;
      border: 1px solid rgba(102, 126, 234, 0.08);
      backdrop-filter: blur(20px);
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.06);
      position: relative;
      overflow: hidden;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: linear-gradient(90deg, #f59e0b, #d97706, #f59e0b);
      }
    }
  }

  // Clean Modern Test Result Styles
  .test-result-section {
    margin-top: 1.5rem !important;

    .result-card {
      background: #ffffff !important;
      border-radius: 12px !important;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
      border: 1px solid #e5e7eb !important;
      overflow: hidden !important;

      &.result-success {
        border-left: 4px solid #10b981 !important;
      }

      &.result-error {
        border-left: 4px solid #ef4444 !important;
      }

      &.result-warning {
        border-left: 4px solid #f59e0b !important;
      }

      &.result-info {
        border-left: 4px solid #3b82f6 !important;
      }

      .result-header {
        display: flex !important;
        justify-content: space-between !important;
        align-items: flex-start !important;
        padding: 1.5rem 1.5rem 1rem 1.5rem !important;
        border-bottom: 1px solid #f3f4f6 !important;

        .header-left {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;

          .status-icon {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;

            &.icon-success {
              background: #d1fae5;
              color: #10b981;
            }

            &.icon-error {
              background: #fee2e2;
              color: #ef4444;
            }

            &.icon-warning {
              background: #fef3c7;
              color: #f59e0b;
            }

            &.icon-info {
              background: #dbeafe;
              color: #3b82f6;
            }
          }

          .header-text {
            .result-title {
              font-size: 1.25rem;
              font-weight: 700;
              color: #111827;
              margin: 0 0 0.25rem 0;
              line-height: 1.3;
            }

            .result-subtitle {
              font-size: 0.875rem;
              color: #6b7280;
              margin: 0;
              line-height: 1.4;
            }
          }
        }

        .status-badge {
          padding: 0.375rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;

          &.badge-success {
            background: #10b981;
            color: white;
          }

          &.badge-error {
            background: #ef4444;
            color: white;
          }

          &.badge-warning {
            background: #f59e0b;
            color: white;
          }

          &.badge-info {
            background: #3b82f6;
            color: white;
          }
        }
      }

      .test-case-info {
        padding: 0 1.5rem 1rem 1.5rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;

        .test-case-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #6b7280;
        }

        .test-case-value {
          font-size: 0.875rem;
          font-weight: 700;
          color: #111827;
          background: #f3f4f6;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
        }
      }

      .result-content {
        padding: 0 1.5rem 1.5rem 1.5rem;

        .info-item {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          margin-bottom: 1rem;

          &:last-child {
            margin-bottom: 0;
          }

          .info-icon {
            color: #6b7280;
            margin-top: 0.125rem;
            flex-shrink: 0;
          }

          .info-content {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 0.25rem;

            .info-label {
              font-size: 0.875rem;
              font-weight: 600;
              color: #374151;
            }

            .info-value {
              font-size: 0.875rem;
              color: #6b7280;
              word-break: break-word;

              &.success {
                color: #10b981;
                font-weight: 600;
                font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
                background: #f0fdf4;
                padding: 0.25rem 0.5rem;
                border-radius: 4px;
                display: inline-block;
              }
            }
          }
        }

        .error-section {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1rem;

          .error-header {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            margin-bottom: 0.75rem;

            .error-icon {
              color: #ef4444;
            }

            .error-title {
              font-size: 0.875rem;
              font-weight: 700;
              color: #dc2626;
            }
          }

          .error-items {
            .error-item {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              padding: 0.5rem 0;
              border-bottom: 1px solid #fecaca;

              &:last-child {
                border-bottom: none;
                padding-bottom: 0;
              }

              &.description-item {
                flex-direction: column;
                align-items: flex-start;
                gap: 0.5rem;
              }

              .error-label {
                font-size: 0.875rem;
                font-weight: 600;
                color: #374151;
                min-width: 100px;
              }

              .error-value {
                font-size: 0.875rem;
                color: #6b7280;
                word-break: break-word;
                text-align: right;
                flex: 1;

                &.status-code,
                &.error-code {
                  background: #fee2e2;
                  color: #dc2626;
                  padding: 0.25rem 0.5rem;
                  border-radius: 4px;
                  font-weight: 600;
                  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
                  display: inline-block;
                }

                &.error-message {
                  color: #dc2626;
                  font-weight: 600;
                }

                &.error-description {
                  color: #6b7280;
                  font-style: italic;
                  line-height: 1.5;
                  text-align: left;
                  background: #ffffff;
                  padding: 0.75rem;
                  border-radius: 4px;
                  border-left: 3px solid #ef4444;
                }
              }
            }
          }
        }
      }
    }
  }

  .modal-footer {
    border: none;
    padding: 2.5rem 3rem;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-top: 1px solid rgba(0, 0, 0, 0.04);
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.1), transparent);
    }

    .footer-info {
      .info-item {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1.25rem 1.5rem;
        background: rgba(102, 126, 234, 0.08);
        border-radius: 12px;
        border: 1px solid rgba(102, 126, 234, 0.12);
        transition: all 0.3s ease;
        max-width: 400px;

        &.executing {
          background: rgba(245, 158, 11, 0.08);
          border-color: rgba(245, 158, 11, 0.15);
          color: #f59e0b;

          .info-text {
            color: #f59e0b;
          }
        }

        .loading-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(245, 158, 11, 0.2);
          border-top: 2px solid #f59e0b;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          flex-shrink: 0;
        }

        .info-icon {
          color: #667eea;
          flex-shrink: 0;
        }

        .info-text {
          color: #475569;
          font-weight: 600;
          font-size: 0.9rem;
          line-height: 1.4;
        }
      }
    }

    .footer-actions {
      display: flex;
      gap: 1.5rem;
      align-items: center;

      .cancel-btn {
        background: linear-gradient(135deg, #64748b 0%, #475569 100%);
        border: none;
        color: white;
        border-radius: 12px;
        padding: 1rem 2.5rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        transition: all 0.3s ease;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        font-size: 0.9rem;
        position: relative;
        overflow: hidden;
        min-width: 140px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: left 0.5s ease;
        }

        &:hover:not(:disabled) {
          transform: translateY(-3px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);

          &::before {
            left: 100%;
          }
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .feather-icon {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
        }
      }

      .execute-test-btn {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        border: none;
        color: white;
        min-width: 200px;
        border-radius: 12px;
        padding: 1rem 2.5rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        transition: all 0.3s ease;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        font-size: 0.9rem;
        position: relative;
        overflow: hidden;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: left 0.5s ease;
        }

        &:hover:not(:disabled) {
          background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
          box-shadow: 0 8px 24px rgba(102, 126, 234, 0.25);
          transform: translateY(-3px);

          &::before {
            left: 100%;
          }
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .loading-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          flex-shrink: 0;
        }

        .feather-icon {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
        }
      }
    }

    // Compact Business Information Styles
    .business-info-compact {
      background: linear-gradient(
        135deg,
        rgba(16, 185, 129, 0.05) 0%,
        rgba(255, 255, 255, 0.95) 100%
      ) !important;
      border: 1px solid rgba(16, 185, 129, 0.2) !important;
      border-radius: 12px !important;
      padding: 1rem !important;
      margin-bottom: 1rem !important;

      .section-title {
        color: #1e293b !important;
        font-weight: 700 !important;
        font-size: 1.1rem !important;
        display: flex !important;
        align-items: center !important;
        margin-bottom: 1rem !important;

        .feather-icon {
          color: #10b981 !important;
        }
      }

      .business-info-row {
        display: grid !important;
        grid-template-columns: 1fr !important;
        gap: 0.5rem !important;
        margin-bottom: 1rem !important;

        .info-item {
          display: flex !important;
          align-items: center !important;
          gap: 0.4rem !important;
          padding: 0.6rem !important;
          background: rgba(255, 255, 255, 0.9) !important;
          border-radius: 6px !important;
          border: 1px solid rgba(16, 185, 129, 0.15) !important;
          min-height: 38px !important;

          .info-icon {
            color: #10b981 !important;
            flex-shrink: 0 !important;
          }

          .info-label {
            font-weight: 600 !important;
            color: #475569 !important;
            font-size: 0.78rem !important;
            white-space: nowrap !important;
            min-width: 50px !important;
          }

          .info-value {
            font-weight: 500 !important;
            color: #1e293b !important;
            font-size: 0.82rem !important;
            word-break: break-all !important;
            flex: 1 !important;

            &.token {
              font-family: monospace !important;
              font-size: 0.8rem !important;
              color: #667eea !important;
              background: rgba(102, 126, 234, 0.1) !important;
              padding: 0.25rem 0.5rem !important;
              border-radius: 4px !important;
            }
          }
        }
      }

      .business-summary-row {
        display: grid !important;
        grid-template-columns: 1fr !important;
        gap: 0.5rem !important;
        padding: 0.5rem !important;
        background: rgba(102, 126, 234, 0.05) !important;
        border-radius: 6px !important;
        border: 1px solid rgba(102, 126, 234, 0.1) !important;

        .summary-item {
          font-size: 0.78rem !important;
          color: #475569 !important;
          text-align: center !important;
          padding: 0.35rem !important;
          background: rgba(255, 255, 255, 0.6) !important;
          border-radius: 4px !important;

          strong {
            color: #1e293b !important;
            font-weight: 700 !important;
            display: block !important;
            margin-bottom: 0.2rem !important;
            font-size: 0.75rem !important;
          }
        }
      }
    }

    // Enhanced Test Case Selection
    .test-case-selection-section {
      background: linear-gradient(
        135deg,
        rgba(102, 126, 234, 0.05) 0%,
        rgba(255, 255, 255, 0.95) 100%
      ) !important;
      border: 1px solid rgba(102, 126, 234, 0.2) !important;
      border-radius: 12px !important;
      padding: 1.25rem !important;
      margin-bottom: 1.25rem !important;

      .section-header {
        text-align: center;
        margin-bottom: 1.5rem !important;

        .test-case-icon-wrapper {
          display: flex;
          justify-content: center;
          margin-bottom: 1rem !important;

          .test-case-icon {
            width: 50px !important;
            height: 50px !important;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
            border-radius: 50% !important;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white !important;
            box-shadow: 0 6px 18px rgba(102, 126, 234, 0.3) !important;
          }
        }

        .section-title {
          color: #1e293b !important;
          font-weight: 700 !important;
          font-size: 1.25rem !important;
          margin-bottom: 0.5rem !important;
        }

        .section-subtitle {
          color: #64748b !important;
          font-size: 0.9rem !important;
          margin: 0 !important;
        }
      }

      .test-case-dropdown-container {
        max-width: 350px;
        margin: 0 auto;

        .form-label {
          font-weight: 600 !important;
          color: #374151 !important;
          margin-bottom: 0.75rem !important;
          font-size: 0.9rem !important;
        }

        .form-text {
          color: #6b7280 !important;
          font-size: 0.8rem !important;
          text-align: center;
          margin-top: 0.75rem !important;
          font-style: italic;
        }
      }
    }

    // Enhanced Execute Button
    .execute-btn-primary {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
      border: none !important;
      border-radius: 12px !important;
      padding: 0.875rem 2rem !important;
      font-weight: 600 !important;
      font-size: 1rem !important;
      box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3) !important;
      transition: all 0.3s ease !important;

      &:hover:not(:disabled) {
        background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%) !important;
        transform: translateY(-2px) !important;
        box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4) !important;
      }

      &:disabled {
        opacity: 0.6 !important;
        cursor: not-allowed !important;
        transform: none !important;
      }
    }
  }
}

// Enhanced loading spinner animation
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

// Test Results Modal Styles
.test-results-modal {
  .modal-dialog {
    max-width: 900px;
  }

  .modal-content {
    border: none;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    overflow: hidden;
  }

  .modal-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    padding: 1.5rem 2rem;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="white" opacity="0.1"/><circle cx="75" cy="75" r="1" fill="white" opacity="0.1"/><circle cx="50" cy="10" r="0.5" fill="white" opacity="0.1"/><circle cx="10" cy="60" r="0.5" fill="white" opacity="0.1"/><circle cx="90" cy="40" r="0.5" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
      opacity: 0.3;
    }

    .modal-title {
      font-weight: 700;
      font-size: 1.5rem;
      position: relative;
      z-index: 1;
    }

    .close {
      color: white;
      opacity: 0.8;
      font-size: 1.5rem;
      position: relative;
      z-index: 1;

      &:hover {
        opacity: 1;
        transform: scale(1.1);
        transition: all 0.2s ease;
      }
    }
  }

  .modal-body {
    padding: 2rem;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.3), transparent);
    }
  }

  .results-summary {
    margin-bottom: 2rem;

    .summary-header {
      display: flex;
      align-items: center;
      padding: 1.5rem;
      border-radius: 16px;
      margin-bottom: 1rem;
      position: relative;
      overflow: hidden;
      backdrop-filter: blur(10px);
      border: 2px solid transparent;
      background-clip: padding-box;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: 16px;
        padding: 2px;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.1));
        mask:
          linear-gradient(#fff 0 0) content-box,
          linear-gradient(#fff 0 0);
        mask-composite: exclude;
        -webkit-mask-composite: xor;
      }

      &.summary-success {
        background: linear-gradient(
          135deg,
          rgba(40, 199, 111, 0.15) 0%,
          rgba(40, 199, 111, 0.05) 100%
        );
        color: #28c76f;
        box-shadow: 0 8px 32px rgba(40, 199, 111, 0.2);
        animation: successPulse 2s ease-in-out infinite;

        .summary-icon {
          animation: bounce 1s ease-in-out infinite;
        }
      }

      &.summary-error {
        background: linear-gradient(
          135deg,
          rgba(255, 71, 87, 0.15) 0%,
          rgba(255, 71, 87, 0.05) 100%
        );
        color: #ff4757;
        box-shadow: 0 8px 32px rgba(255, 71, 87, 0.2);
        animation: errorShake 0.5s ease-in-out;

        .summary-icon {
          animation: shake 0.5s ease-in-out infinite;
        }
      }

      .summary-icon {
        flex-shrink: 0;
        margin-right: 1rem;
        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
      }

      .summary-title {
        margin: 0;
        font-weight: 700;
        font-size: 1.25rem;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
      }
    }

    .summary-message {
      font-size: 1.1rem;
      color: #5e5873;
      font-weight: 500;
      text-align: center;
      padding: 1rem;
      background: rgba(255, 255, 255, 0.7);
      border-radius: 12px;
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.3);
    }
  }

  .test-cases-list {
    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 2rem;
      padding: 1.5rem;
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.9) 0%,
        rgba(248, 250, 252, 0.9) 100%
      );
      border-radius: 16px;
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.3);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);

      .section-title {
        color: #5e5873;
        font-weight: 700;
        font-size: 1.25rem;
        margin: 0;
        display: flex;
        align-items: center;
        gap: 0.75rem;

        .section-icon {
          color: #667eea;
        }
      }

      .test-summary-stats {
        display: flex;
        gap: 1.5rem;

        .stat-item {
          text-align: center;
          padding: 0.75rem 1rem;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;

          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
          }

          .stat-number {
            display: block;
            font-size: 1.5rem;
            font-weight: 700;
            color: #5e5873;
            margin-bottom: 0.25rem;

            &.error {
              color: #ff4757;
            }

            &.success {
              color: #28c76f;
            }
          }

          .stat-label {
            font-size: 0.875rem;
            color: #6c757d;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
        }
      }
    }

    .test-cases-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
      gap: 1.5rem;
      margin-bottom: 2rem;
    }

    .test-case-card {
      border: none;
      border-radius: 20px;
      padding: 1.5rem;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      overflow: hidden;
      backdrop-filter: blur(10px);
      background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.9) 0%,
        rgba(248, 250, 252, 0.9) 100%
      );
      border: 1px solid rgba(255, 255, 255, 0.3);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: 20px;
        padding: 2px;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.1));
        mask:
          linear-gradient(#fff 0 0) content-box,
          linear-gradient(#fff 0 0);
        mask-composite: exclude;
        -webkit-mask-composite: xor;
      }

      &:hover {
        transform: translateY(-6px);
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
      }

      &.test-case-success {
        background: linear-gradient(
          135deg,
          rgba(40, 199, 111, 0.1) 0%,
          rgba(40, 199, 111, 0.05) 100%
        );
        border-color: rgba(40, 199, 111, 0.2);
        box-shadow: 0 8px 32px rgba(40, 199, 111, 0.15);

        &::before {
          background: linear-gradient(135deg, rgba(40, 199, 111, 0.3), rgba(40, 199, 111, 0.1));
        }
      }

      &.test-case-error {
        background: linear-gradient(
          135deg,
          rgba(255, 71, 87, 0.1) 0%,
          rgba(255, 71, 87, 0.05) 100%
        );
        border-color: rgba(255, 71, 87, 0.2);
        box-shadow: 0 8px 32px rgba(255, 71, 87, 0.15);

        &::before {
          background: linear-gradient(135deg, rgba(255, 71, 87, 0.3), rgba(255, 71, 87, 0.1));
        }
      }

      .test-case-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 1.5rem;
        position: relative;
        z-index: 1;

        .test-case-info {
          display: flex;
          align-items: center;
          gap: 1rem;

          .test-case-number {
            .number-badge {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 48px;
              height: 48px;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;
              border-radius: 50%;
              font-weight: 700;
              font-size: 1.1rem;
              box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
              transition: all 0.3s ease;

              &:hover {
                transform: scale(1.1);
                box-shadow: 0 6px 24px rgba(102, 126, 234, 0.4);
              }
            }
          }

          .test-case-details {
            .test-case-title {
              color: #5e5873;
              font-weight: 700;
              font-size: 1.1rem;
              margin: 0 0 0.5rem 0;
            }

            .test-case-meta {
              .test-timestamp {
                color: #6c757d;
                font-size: 0.875rem;
                font-weight: 500;
              }
            }
          }
        }

        .test-case-status {
          .status-indicator {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem 1rem;
            border-radius: 20px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-size: 0.875rem;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            transition: all 0.3s ease;

            &.status-success {
              background: linear-gradient(135deg, #28c76f 0%, #20c997 100%);
              color: white;

              &:hover {
                transform: translateY(-2px);
                box-shadow: 0 6px 20px rgba(40, 199, 111, 0.3);
              }
            }

            &.status-error {
              background: linear-gradient(135deg, #ff4757 0%, #ff3838 100%);
              color: white;

              &:hover {
                transform: translateY(-2px);
                box-shadow: 0 6px 20px rgba(255, 71, 87, 0.3);
              }
            }

            .status-icon {
              flex-shrink: 0;
            }

            .status-text {
              font-weight: 700;
            }
          }
        }
      }

      .test-case-content {
        position: relative;
        z-index: 1;
        margin-bottom: 1.5rem;

        .test-case-message {
          margin-bottom: 1rem;

          .message-text {
            margin: 0;
            color: #5e5873;
            font-weight: 500;
            line-height: 1.6;
            font-size: 1rem;
          }
        }

        .fbr-response-section {
          background: rgba(255, 255, 255, 0.8);
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
          overflow: hidden;

          .response-header {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.75rem 1rem;
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            border-bottom: 1px solid rgba(0, 0, 0, 0.05);

            .response-icon {
              color: #6c757d;
            }

            .response-title {
              color: #495057;
              font-weight: 600;
              font-size: 0.875rem;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
          }

          .response-content {
            padding: 1rem;

            .invoice-info,
            .validation-details,
            .error-details,
            .invoice-statuses,
            .raw-response {
              margin-bottom: 1.5rem;

              .invoice-title,
              .validation-title,
              .error-title,
              .statuses-title,
              .raw-title {
                color: #495057;
                font-weight: 700;
                font-size: 1rem;
                margin-bottom: 1rem;
                padding-bottom: 0.5rem;
                border-bottom: 2px solid rgba(23, 162, 184, 0.2);
              }

              .detail-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 0.5rem 0;
                border-bottom: 1px solid rgba(0, 0, 0, 0.05);

                .detail-label {
                  font-weight: 600;
                  color: #6c757d;
                  font-size: 0.875rem;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                }

                .detail-value {
                  font-weight: 500;
                  color: #495057;
                  font-size: 0.9rem;
                  text-align: right;
                  max-width: 60%;
                  word-break: break-word;

                  &.text-danger {
                    color: #dc3545;
                    font-weight: 600;
                  }
                }
              }

              .invoice-details,
              .validation-info,
              .error-info {
                background: rgba(255, 255, 255, 0.6);
                border-radius: 8px;
                padding: 1rem;
                border: 1px solid rgba(0, 0, 0, 0.05);
              }

              .invoice-statuses {
                .statuses-list {
                  .status-item {
                    background: rgba(255, 255, 255, 0.8);
                    border-radius: 8px;
                    padding: 1rem;
                    margin-bottom: 1rem;
                    border: 1px solid rgba(0, 0, 0, 0.05);

                    .status-header {
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      margin-bottom: 1rem;
                      padding-bottom: 0.5rem;
                      border-bottom: 1px solid rgba(0, 0, 0, 0.1);

                      .item-sno {
                        font-weight: 600;
                        color: #495057;
                        font-size: 0.9rem;
                      }

                      .status-badge {
                        padding: 0.25rem 0.75rem;
                        border-radius: 20px;
                        font-size: 0.75rem;
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;

                        &.status-Valid {
                          background: rgba(40, 199, 111, 0.2);
                          color: #28c76f;
                          border: 1px solid rgba(40, 199, 111, 0.3);
                        }

                        &.status-Invalid {
                          background: rgba(255, 71, 87, 0.2);
                          color: #ff4757;
                          border: 1px solid rgba(255, 71, 87, 0.3);
                        }
                      }
                    }

                    .status-details {
                      .detail-row {
                        padding: 0.25rem 0;
                        border-bottom: none;

                        .detail-label {
                          font-size: 0.8rem;
                          color: #6c757d;
                        }

                        .detail-value {
                          font-size: 0.85rem;
                          color: #495057;
                        }
                      }
                    }
                  }
                }
              }

              .response-code {
                margin: 0;
                font-size: 0.875rem;
                color: #495057;
                background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                padding: 1rem;
                border-radius: 8px;
                border: 1px solid rgba(0, 0, 0, 0.05);
                max-height: 200px;
                overflow-y: auto;
                font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
                box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);
                line-height: 1.5;
              }
            }
          }
        }
      }

      .test-case-footer {
        position: relative;
        z-index: 1;

        .test-case-actions {
          display: flex;
          gap: 0.75rem;

          .action-btn {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem 1rem;
            border: none;
            border-radius: 12px;
            font-weight: 600;
            font-size: 0.875rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

            &:hover {
              transform: translateY(-2px);
            }

            &.view-btn {
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              color: white;

              &:hover {
                box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);
              }
            }

            &.retry-btn {
              background: linear-gradient(135deg, #ffa726 0%, #ff9800 100%);
              color: white;

              &:hover {
                box-shadow: 0 6px 20px rgba(255, 167, 38, 0.3);
              }
            }
          }
        }
      }
    }
  }

  .test-case-ids {
    margin-top: 2rem;
    padding: 1.5rem;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 16px;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.3);

    h6 {
      color: #5e5873;
      font-weight: 700;
      margin-bottom: 1rem;
      text-align: center;
    }

    .test-case-ids-list {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
      justify-content: center;

      .badge {
        padding: 0.5rem 1rem;
        border-radius: 20px;
        font-weight: 600;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        transition: all 0.3s ease;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
        }
      }
    }
  }

  .test-loading {
    padding: 3rem 2rem;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%);
    border-radius: 20px;
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);

    .loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 300px;
    }

    .loading-animation {
      text-align: center;
      max-width: 400px;
    }

    .loading-spinner {
      position: relative;
      margin-bottom: 2rem;
      display: flex;
      justify-content: center;
      align-items: center;
      height: 120px;

      .spinner-ring {
        position: relative;
        width: 80px;
        height: 80px;
        border-radius: 50%;
        background: conic-gradient(from 0deg, #667eea, #764ba2, #667eea);
        animation: spin 2s linear infinite;
        padding: 4px;

        &::before {
          content: '';
          position: absolute;
          top: 4px;
          left: 4px;
          right: 4px;
          bottom: 4px;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-radius: 50%;
          z-index: 1;
        }

        .spinner-ring-inner {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 20px;
          height: 20px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 50%;
          z-index: 2;
          animation: pulse 1.5s ease-in-out infinite;
        }
      }

      .spinner-dots {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        gap: 8px;
        z-index: 3;

        .dot {
          width: 8px;
          height: 8px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 50%;
          animation: dotBounce 1.4s ease-in-out infinite both;

          &:nth-child(1) {
            animation-delay: -0.32s;
          }
          &:nth-child(2) {
            animation-delay: -0.16s;
          }
          &:nth-child(3) {
            animation-delay: 0s;
          }
        }
      }
    }

    .loading-content {
      .loading-title {
        color: #5e5873;
        font-weight: 700;
        font-size: 1.5rem;
        margin-bottom: 1rem;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        animation: titleGlow 2s ease-in-out infinite;
      }

      .loading-text {
        color: #6c757d;
        font-size: 1rem;
        font-weight: 500;
        margin-bottom: 2rem;
        line-height: 1.6;
        animation: fadeInOut 2s ease-in-out infinite;
      }

      .loading-progress {
        .progress-bar {
          width: 100%;
          height: 8px;
          background: rgba(108, 117, 125, 0.1);
          border-radius: 10px;
          overflow: hidden;
          margin-bottom: 1rem;
          position: relative;

          &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
            animation: shimmer 2s ease-in-out infinite;
          }

          .progress-fill {
            height: 100%;
            background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
            border-radius: 10px;
            width: 0%;
            animation: progressFill 3s ease-in-out infinite;
            position: relative;
            overflow: hidden;

            &::after {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: linear-gradient(
                90deg,
                transparent,
                rgba(255, 255, 255, 0.4),
                transparent
              );
              animation: progressShimmer 1.5s ease-in-out infinite;
            }
          }
        }

        .progress-text {
          color: #6c757d;
          font-size: 0.9rem;
          font-weight: 500;
          animation: fadeInOut 2s ease-in-out infinite;
        }
      }
    }
  }

  .modal-footer {
    border: none;
    padding: 1.5rem 2rem;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border-top: 1px solid rgba(0, 0, 0, 0.05);

    .btn {
      border-radius: 12px;
      padding: 0.75rem 2rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      transition: all 0.3s ease;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
      }
    }
  }
}

// Animations
@keyframes successPulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}

@keyframes errorShake {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-5px);
  }
  75% {
    transform: translateX(5px);
  }
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

@keyframes shake {
  0%,
  100% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(-5deg);
  }
  75% {
    transform: rotate(5deg);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

@keyframes fadeInOut {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes dotBounce {
  0%,
  80%,
  100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

@keyframes titleGlow {
  0%,
  100% {
    filter: brightness(1);
    transform: scale(1);
  }
  50% {
    filter: brightness(1.2);
    transform: scale(1.02);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes progressFill {
  0% {
    width: 0%;
  }
  50% {
    width: 70%;
  }
  100% {
    width: 100%;
  }
}

@keyframes progressShimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.error-state {
  padding: 3rem 2rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%);
  border-radius: 20px;
  backdrop-filter: blur(20px);
  border: 2px solid rgba(220, 53, 69, 0.1);
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.08);

  .error-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 80px;
    height: 80px;
    background: rgba(220, 53, 69, 0.1);
    border-radius: 50%;
    margin: 0 auto;
    animation: pulse 2s ease-in-out infinite;

    @keyframes pulse {
      0%,
      100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.05);
      }
    }
  }

  h5 {
    font-weight: 700;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 2rem;
  }
}

/* Enhanced Business Information Styles */
.business-info-enhanced {
  background: #f8fafc;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #e2e8f0;
}

.business-header {
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e2e8f0;
}

.header-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  color: white;
}

.header-content {
  flex: 1;
}

.business-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.25rem 0;
}

.business-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.business-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.detail-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
}

.detail-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.detail-icon {
  width: 32px;
  height: 32px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #6b7280;
}

.detail-content {
  flex: 1;
  min-width: 0;
}

.detail-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
}

.detail-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  word-break: break-word;
}

.detail-value.token-value {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.8rem;
  color: #6b7280;
  word-break: break-all;
}

.business-summary {
  background: white;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e5e7eb;
}

.summary-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  color: #667eea;
  font-weight: 600;
  font-size: 0.875rem;
}

.summary-header svg {
  margin-right: 0.5rem;
}

.summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 1rem;
}

.summary-item {
  text-align: center;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.summary-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
}

.summary-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  word-break: break-word;
}

/* Enhanced Test Results Styles */
.test-result-enhanced {
  margin-top: 1.5rem;
}

.result-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.result-card.result-error {
  border-left: 4px solid #ef4444;
}

.result-card.result-success {
  border-left: 4px solid #10b981;
}

.result-card.result-info {
  border-left: 4px solid #3b82f6;
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 1.5rem;
  border-bottom: 1px solid #f3f4f6;
}

.header-left {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.status-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.status-icon.icon-error {
  background: #fee2e2;
  color: #ef4444;
}

.status-icon.icon-success {
  background: #d1fae5;
  color: #10b981;
}

.status-icon.icon-info {
  background: #dbeafe;
  color: #3b82f6;
}

.header-text {
  flex: 1;
}

.result-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.25rem 0;
  line-height: 1.3;
}

.result-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.4;
}

.status-badge {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.badge-error {
  background: #ef4444;
  color: white;
}

.status-badge.badge-success {
  background: #10b981;
  color: white;
}

.status-badge.badge-info {
  background: #3b82f6;
  color: white;
}

.test-case-info {
  padding: 0 1.5rem 1rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.test-case-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
}

.test-case-value {
  font-size: 0.875rem;
  font-weight: 700;
  color: #111827;
  background: #f3f4f6;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.result-content {
  padding: 0 1.5rem 1.5rem 1.5rem;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-icon {
  color: #6b7280;
  margin-top: 0.125rem;
  flex-shrink: 0;
}

.info-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.info-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.info-value {
  font-size: 0.875rem;
  color: #6b7280;
  word-break: break-word;
}

.info-value.success {
  color: #10b981;
  font-weight: 600;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  background: #f0fdf4;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  display: inline-block;
}

.error-section {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.error-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.error-icon {
  color: #ef4444;
}

.error-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: #dc2626;
}

.error-items {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.error-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.5rem 0;
  border-bottom: 1px solid #fecaca;
}

.error-item:last-child {
  border-bottom: none;
}

.error-item.description-item {
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
}

.error-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  min-width: 100px;
}

.error-value {
  font-size: 0.875rem;
  color: #dc2626;
  font-weight: 600;
  text-align: right;
  flex: 1;
  word-break: break-word;
}

.error-value.status-code,
.error-value.error-code {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  background: #fee2e2;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  display: inline-block;
}

.error-value.error-description {
  color: #6b7280;
  font-style: italic;
  line-height: 1.5;
  text-align: left;
  background: #ffffff;
  padding: 0.75rem;
  border-radius: 4px;
  border-left: 3px solid #ef4444;
  word-break: break-word;
}

/* Enhanced Test Results Modal Styles */
.test-results-enhanced {
  padding: 0;
}

.results-summary-enhanced {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #e2e8f0;
}

.summary-header-enhanced {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.summary-icon-container {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.summary-header-enhanced.error .summary-icon-container {
  background: #fee2e2;
  color: #ef4444;
}

.summary-header-enhanced.success .summary-icon-container {
  background: #d1fae5;
  color: #10b981;
}

.summary-header-enhanced.info .summary-icon-container {
  background: #dbeafe;
  color: #3b82f6;
}

.summary-content {
  flex: 1;
}

.summary-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
}

.summary-message {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

/* Test Execution Report Styles */
.test-execution-report {
  background: #ffffff;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.report-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f3f4f6;
}

.report-title-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.report-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.report-content {
  flex: 1;
}

.report-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.25rem 0;
}

.report-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.test-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
}

.stat-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.stat-card.total {
  background: #f8fafc;
  border-color: #d1d5db;
}

.stat-card.failed {
  background: #fef2f2;
  border-color: #fecaca;
}

.stat-card.passed {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.stat-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-card.total .stat-icon {
  background: #e5e7eb;
  color: #6b7280;
}

.stat-card.failed .stat-icon {
  background: #fee2e2;
  color: #ef4444;
}

.stat-card.passed .stat-icon {
  background: #d1fae5;
  color: #10b981;
}

.stat-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.stat-number {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
}

.stat-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Execution Summary Enhanced */
.execution-summary-enhanced {
  margin-bottom: 1.5rem;
}

.summary-card-enhanced {
  background: #ffffff;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.summary-header-enhanced {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f3f4f6;
}

.summary-icon {
  width: 32px;
  height: 32px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
}

.summary-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.summary-details-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.summary-detail-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.detail-icon {
  width: 28px;
  height: 28px;
  background: #e5e7eb;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  flex-shrink: 0;
}

.detail-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.detail-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  word-break: break-word;
}

.detail-value.success-rate {
  color: #10b981;
  font-size: 1rem;
  font-weight: 700;
}

/* Individual Test Results */
.individual-test-results {
  background: #ffffff;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.results-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f3f4f6;
}

.results-title-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.results-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.results-content {
  flex: 1;
}

.results-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.25rem 0;
}

.results-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.test-cases-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}

.test-case-card {
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  transition: all 0.2s ease;
}

.test-case-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.test-case-card.error {
  border-left: 4px solid #ef4444;
}

.test-case-card.success {
  border-left: 4px solid #10b981;
}

.test-case-card.info {
  border-left: 4px solid #3b82f6;
}

.test-case-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}

.test-case-number {
  display: flex;
  align-items: center;
}

.number-badge {
  width: 32px;
  height: 32px;
  background: #e5e7eb;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 700;
  color: #6b7280;
}

.test-case-card.error .number-badge {
  background: #fee2e2;
  color: #ef4444;
}

.test-case-card.success .number-badge {
  background: #d1fae5;
  color: #10b981;
}

.test-case-card.info .number-badge {
  background: #dbeafe;
  color: #3b82f6;
}

.test-case-status {
  display: flex;
  align-items: center;
}

.status-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.error {
  background: #ef4444;
  color: white;
}

.status-badge.success {
  background: #10b981;
  color: white;
}

.status-badge.info {
  background: #3b82f6;
  color: white;
}

.test-case-body {
  padding: 1rem;
}

.test-case-title {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 0.5rem 0;
}

.test-case-message {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1rem 0;
  line-height: 1.5;
}

.test-case-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.test-timestamp {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: #9ca3af;
}

.meta-icon {
  color: #9ca3af;
}

/* Force override for test result styles */
.custom-test-modal .test-result-section .result-card {
  background: #ffffff !important;
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  border: 1px solid #e5e7eb !important;
  overflow: hidden !important;
}

.custom-test-modal .test-result-section .result-card.result-success {
  border-left: 4px solid #10b981 !important;
}

.custom-test-modal .test-result-section .result-card.result-error {
  border-left: 4px solid #ef4444 !important;
}

.custom-test-modal .test-result-section .result-card.result-warning {
  border-left: 4px solid #f59e0b !important;
}

.custom-test-modal .test-result-section .result-card.result-info {
  border-left: 4px solid #3b82f6 !important;
}

.custom-test-modal .test-result-section .result-header {
  display: flex !important;
  justify-content: space-between !important;
  align-items: flex-start !important;
  padding: 1.5rem 1.5rem 1rem 1.5rem !important;
  border-bottom: 1px solid #f3f4f6 !important;
}

.custom-test-modal .test-result-section .header-left {
  display: flex !important;
  align-items: flex-start !important;
  gap: 0.75rem !important;
}

.custom-test-modal .test-result-section .status-icon {
  width: 40px !important;
  height: 40px !important;
  border-radius: 50% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
}

.custom-test-modal .test-result-section .status-icon.icon-success {
  background: #d1fae5 !important;
  color: #10b981 !important;
}

.custom-test-modal .test-result-section .status-icon.icon-error {
  background: #fee2e2 !important;
  color: #ef4444 !important;
}

.custom-test-modal .test-result-section .status-icon.icon-warning {
  background: #fef3c7 !important;
  color: #f59e0b !important;
}

.custom-test-modal .test-result-section .status-icon.icon-info {
  background: #dbeafe !important;
  color: #3b82f6 !important;
}

.custom-test-modal .test-result-section .result-title {
  font-size: 1.25rem !important;
  font-weight: 700 !important;
  color: #111827 !important;
  margin: 0 0 0.25rem 0 !important;
  line-height: 1.3 !important;
}

.custom-test-modal .test-result-section .result-subtitle {
  font-size: 0.875rem !important;
  color: #6b7280 !important;
  margin: 0 !important;
  line-height: 1.4 !important;
}

.custom-test-modal .test-result-section .status-badge {
  padding: 0.375rem 0.75rem !important;
  border-radius: 20px !important;
  font-size: 0.75rem !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
}

.custom-test-modal .test-result-section .status-badge.badge-success {
  background: #10b981 !important;
  color: white !important;
}

.custom-test-modal .test-result-section .status-badge.badge-error {
  background: #ef4444 !important;
  color: white !important;
}

.custom-test-modal .test-result-section .status-badge.badge-warning {
  background: #f59e0b !important;
  color: white !important;
}

.custom-test-modal .test-result-section .status-badge.badge-info {
  background: #3b82f6 !important;
  color: white !important;
}

.custom-test-modal .test-result-section .test-case-info {
  padding: 0 1.5rem 1rem 1.5rem !important;
  display: flex !important;
  align-items: center !important;
  gap: 0.5rem !important;
}

.custom-test-modal .test-result-section .test-case-label {
  font-size: 0.875rem !important;
  font-weight: 600 !important;
  color: #6b7280 !important;
}

.custom-test-modal .test-result-section .test-case-value {
  font-size: 0.875rem !important;
  font-weight: 700 !important;
  color: #111827 !important;
  background: #f3f4f6 !important;
  padding: 0.25rem 0.5rem !important;
  border-radius: 4px !important;
}

.custom-test-modal .test-result-section .result-content {
  padding: 0 1.5rem 1.5rem 1.5rem !important;
}

.custom-test-modal .test-result-section .info-item {
  display: flex !important;
  align-items: flex-start !important;
  gap: 0.75rem !important;
  margin-bottom: 1rem !important;
}

.custom-test-modal .test-result-section .info-item:last-child {
  margin-bottom: 0 !important;
}

.custom-test-modal .test-result-section .info-icon {
  color: #6b7280 !important;
  margin-top: 0.125rem !important;
  flex-shrink: 0 !important;
}

.custom-test-modal .test-result-section .info-content {
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 0.25rem !important;
}

.custom-test-modal .test-result-section .info-label {
  font-size: 0.875rem !important;
  font-weight: 600 !important;
  color: #374151 !important;
}

.custom-test-modal .test-result-section .info-value {
  font-size: 0.875rem !important;
  color: #6b7280 !important;
  word-break: break-word !important;
}

.custom-test-modal .test-result-section .info-value.success {
  color: #10b981 !important;
  font-weight: 600 !important;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
  background: #f0fdf4 !important;
  padding: 0.25rem 0.5rem !important;
  border-radius: 4px !important;
  display: inline-block !important;
}

.custom-test-modal .test-result-section .error-section {
  background: #fef2f2 !important;
  border: 1px solid #fecaca !important;
  border-radius: 8px !important;
  padding: 1rem !important;
  margin-bottom: 1rem !important;
}

.custom-test-modal .test-result-section .error-header {
  display: flex !important;
  align-items: center !important;
  gap: 0.5rem !important;
  margin-bottom: 0.75rem !important;
}

.custom-test-modal .test-result-section .error-icon {
  color: #ef4444 !important;
}

.custom-test-modal .test-result-section .error-title {
  font-size: 0.875rem !important;
  font-weight: 700 !important;
  color: #dc2626 !important;
}

.custom-test-modal .test-result-section .error-items .error-item {
  display: flex !important;
  justify-content: space-between !important;
  align-items: flex-start !important;
  padding: 0.5rem 0 !important;
  border-bottom: 1px solid #fecaca !important;
}

.custom-test-modal .test-result-section .error-items .error-item:last-child {
  border-bottom: none !important;
  padding-bottom: 0 !important;
}

.custom-test-modal .test-result-section .error-items .error-item.description-item {
  flex-direction: column !important;
  align-items: flex-start !important;
  gap: 0.5rem !important;
}

.custom-test-modal .test-result-section .error-label {
  font-size: 0.875rem !important;
  font-weight: 600 !important;
  color: #374151 !important;
  min-width: 100px !important;
}

.custom-test-modal .test-result-section .error-value {
  font-size: 0.875rem !important;
  color: #6b7280 !important;
  word-break: break-word !important;
  text-align: right !important;
  flex: 1 !important;
}

.custom-test-modal .test-result-section .error-value.status-code,
.custom-test-modal .test-result-section .error-value.error-code {
  background: #fee2e2 !important;
  color: #dc2626 !important;
  padding: 0.25rem 0.5rem !important;
  border-radius: 4px !important;
  font-weight: 600 !important;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
  display: inline-block !important;
}

.custom-test-modal .test-result-section .error-value.error-message {
  color: #dc2626 !important;
  font-weight: 600 !important;
}

.custom-test-modal .test-result-section .error-value.error-description {
  color: #6b7280 !important;
  font-style: italic !important;
  line-height: 1.5 !important;
  text-align: left !important;
  background: #ffffff !important;
  padding: 0.75rem !important;
  border-radius: 4px !important;
  border-left: 3px solid #ef4444 !important;
}
</style>